import { useState, useMemo, useCallback, useEffect, useRef, Fragment } from 'react'
import { flushSync } from 'react-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import usePreferencesStore from '@store/preferencesStore'
import ColumnValuePicker from '@components/common/ColumnValuePicker'
import ControlCheckbox, { TOGGLE_ROW } from '@components/common/ControlCheckbox'
import { HANDLE_BOX_PX3, DIVIDER_BASE, DIVIDER_RESTING } from '@components/common/gridHeaderChrome'
import PopoverMenu from '@components/common/PopoverMenu'
// D3 (2026-08-27): the header info icon's gate, shared with the flat grid so the
// 86 grouped reports and the 33 flat ones cannot drift. A plain leaf module rather
// than a helper exported from either grid — see the rationale on the predicate.
import { isAuthoredDefinition } from './columnDefinitionGate'
import { SlidersIcon, FunnelIcon } from './queue/queueIcons'
import {
  PIN_CELL_CLASS,
  pinFlagName,
  pinFlagsToClearOnUnfreeze,
  pinToggleTargetId,
  resolvePinnedColumnId,
  orderWithPinFirst,
  usePinnedScrollShadow,
} from '@utils/columnPinning'
import { useReportExportView } from './reportExportViewContext'
import { useAliasedGridColumns } from './aliasedGridColumns'
import { useReportStripActions } from './reportRunStateContext'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'
import { trackGridTransform } from '@/analytics/gridTransforms'
// The four view options' factory defaults and the missing-hook caveat, shared
// with ReportDataGrid so the 86 grouped reports and the 33 flat ones cannot
// drift — see that module's note.
import { GRID_DISPLAY_DEFAULTS, PAGE_EXTRAS_NOT_RESET_NOTE, THOUSANDS_PAGE_FLAG } from './gridDisplayDefaults'
// Spec 213: where display pages break (a 25-group page that also stops at a row budget).
import { groupPageStarts } from './groupPages'
import {
  stripHtml,
  numericSortValue,
  formatLikeSample,
  columnDefinition,
  columnDataKinds,
  totaledColumnIds,
  columnSamples,
  metadataDefaultSort,
  isColumnFilterable,
  isNumericColumn,
  currencyEligible,
  currencyMarkedColumnIds,
  currencyToggleActs,
  numericTotalColumnIds,
  showDollarNumeric,
  declaredAmountColumnIds,
  isHideableZeroRow,
  findStatusColumn,
  isInactiveStatusValue,
  isNameColumn,
  distinctDisplayValues,
  filterIsActive,
  matchColumnFilter,
  withCurrencySymbol,
  stripCurrencySymbol,
  displayHasCurrency,
  groupedDisplay,
  thousandsToggleActs,
  formatCount,
  findAgingColumn,
  isStaleValue,
  parseCellDate,
  AGING_PRESET_DAYS,
  MAX_AUTO_COLUMN_PX,
  compareCellValues,
} from './reportGridUtils'
// Spec 127 S3: the export view is built by a pure module so a caller with no
// mounted grid (the queue drawer) gets the same view. The row/group primitives
// below live there too, so the renderer and the export walk share ONE definition
// of each (the positional collapse key above all — see groupRowKey).
import {
  cellById,
  cellNumber,
  collectLeaves,
  groupRowKey,
  groupFooterLines,
  reportFooterLines,
  computeSummable,
  footerRecompute,
  buildGroupedExportView,
  buildStatementIndex,
  treeZeroKeep,
} from './groupedExportView'

/**
 * Reporting V2 — universal controls for GROUPED CB reports (CC-49381 / Issue #6 WS1).
 *
 * Renders CB's server-computed group structure verbatim — group headers, group
 * footer **subtotals**, and the report **grand total** come straight from the
 * server payload. By default they are NEVER recomputed client-side (preserves
 * CB's authoritative math; the "99% right = 100% wrong" guardrail).
 *
 * Controls layered on top, applied WITHIN each group:
 *   - click-to-sort (ascending-first); a leaf carrying `pin: 'first' | 'last'`
 *     stays at that end of its group whatever the sort (CC-54054)
 *   - forgiving per-column quick-filter (shared with the flat grid)
 *   - column show/hide (persisted per report via preferencesStore)
 *   - collapsible groups
 *
 * Filtered-totals coherence (CC-49382 + CC-49456): when a filter hides rows,
 * subtotals and the grand total re-sum to the visible rows BY DEFAULT (CC-49456 —
 * the usual intent when filtering); a toggle lets the user opt back to the full
 * unfiltered server totals. Recompute is SUM-only (a column is re-summed only if
 * its server grand total equals the sum of every row — averages/rates keep their
 * server value with an explanatory marker).
 *
 * Deferred (CC-49380): column drag-reorder and width persistence. Mixed groups
 * (leaf rows interleaved with sub-groups under one parent) render leaves first
 * then sub-groups; CB rarely mixes the two at one level.
 */

const alignClass = (col) => {
  switch (col?.columnAlignment) {
    case 'RIGHT': return 'text-right'
    case 'CENTER': return 'text-center'
    default: return 'text-left'
  }
}

// `cellById`, `cellNumber`, `collectLeaves`, `groupRowKey` and `groupFooterLines`
// MOVED to ./groupedExportView (spec 127 S3), imported above. `groupRowKey` is
// still the SINGLE SOURCE OF TRUTH for a group's collapse key (CC-49843): the
// renderer (renderRows) and the export view both derive keys through it, and the
// key is positional (path + level + index), so BOTH sides must walk the same base
// list — `rows` when not paging, `visibleTop` when paging (see `collapseBase`).

// Sort key (mirrors the flat grid via reportGridUtils.numericSortValue).
const sortKey = (cell) => numericSortValue(cell?.dataValue, stripHtml(cell?.displayValue).toLowerCase())

// CC-49753: shared comparator — numbers first, then digit-leading text, then
// letters; entity-decoded so escaped quotes don't break alphabetical order.
const compare = compareCellValues

// Spec 127 S7: a statement tree row keys on its CB-derived id, so hiding its
// siblings never remounts the row that owns the focused roll-up toggle.
const treeRowKey = (tree) => `t-${tree.id}-${tree.kind}`

// Draggable + resizable header cell (CC-49459 — flat-grid parity on the
// grouped grid's hand-rolled table). Mirrors DataGrid's DraggableHeader UX:
// ⠿ grab handle to reorder; right-edge handle to resize (drag / double-click
// reset / arrow keys, WCAG 2.1.1).
const GroupedHeaderCell = ({ id, label, width, onResize, onResetWidth, ariaSort, pinned = false, isLastHeader = false, children }) => {
  // CC-49462: a frozen column is hoisted to the left edge and taken out of the
  // reorder set (it stays resizable — only dragging is disabled).
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: pinned })
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // An explicit (user-resized) width wins outright; otherwise cap how far the
    // column may auto-grow so one long value can't starve its neighbours.
    ...(width ? { width } : { maxWidth: MAX_AUTO_COLUMN_PX }),
  }
  const startDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const th = e.currentTarget.closest('th')
    const startW = width || th?.offsetWidth || 120
    const startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
    const move = (ev) => {
      const x = ev.clientX ?? ev.touches?.[0]?.clientX ?? 0
      onResize(Math.max(40, startW + (x - startX)))
    }
    const up = () => {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
      document.removeEventListener('touchmove', move)
      document.removeEventListener('touchend', up)
    }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
    document.addEventListener('touchmove', move)
    document.addEventListener('touchend', up)
  }
  return (
    <th
      ref={setNodeRef}
      style={style}
      scope="col"
      // CC-53347: the seed measurement needs to attribute a rendered width to a
      // column id, and the header's OWN cells are the width authority (the second
      // header row is the column-filter row). Read by measureRenderedWidths.
      data-col-id={id}
      // CC-50173: name the header cell explicitly. Its contents are the ⠿ drag
      // handle (or CC-49462's ▌ frozen marker) plus (often) the "About {col}: …"
      // info icon, so the computed name was the label with every decoration read
      // out after it. Same fix as DataGrid's DraggableHeader — see the longer note
      // there, including why naming the handle makes this label load-bearing.
      aria-label={label}
      aria-sort={ariaSort}
      // B4: this is the ONLY rule under the header row. The <thead> used to carry
      // a border-b of its own as well, so the two stacked into a ~2px composite
      // that the 33 flat reports never drew — see the note on the <thead> below.
      className={`relative px-3 py-2 border-b border-rc-line select-none ${pinned ? PIN_CELL_CLASS : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        {pinned ? (
          <span
            role="img"
            aria-label={`${label} is frozen and stays visible when you scroll right`}
            title="Frozen column — stays visible when you scroll right"
            /* spec 121 gap 1 + gap 4, 2026-09-09. HANDLE_BOX_PX3 (not _PX4) purely so
               a pinned column's label lines up with every other column's now that the
               reorder target beside it is 24 wide — this marker is a role="img", not a
               control, and owes SC 2.5.8 nothing.
               text-rc-muted was text-gray-400: #99a1af (Tailwind 4.2.1's oklch value,
               NOT v3's #9ca3af) measures 2.490:1 on this cell's opaque #f9fafb, under
               SC 1.4.11's 3.0:1, which a graphical object conveying a STATE owes.
               #6b7280 is 4.626:1 — the minimum compliant EXISTING token, and the same
               one the sort mark already wears. Full reasoning at DataGrid.jsx's twin. */
            className={`${HANDLE_BOX_PX3} text-rc-muted text-[10px] leading-none`}
          >
            {/* aria-hidden so the glyph does not read out after the label the
                role="img" already supplies (CC-50173). */}
            <span aria-hidden="true">▌</span>
          </span>
        ) : (
          <span
            {...attributes}
            {...listeners}
            // Focusable role="button" from useSortable — it must not be named "⠿".
            //
            // spec 121 gap 1 (2026-09-09): B2's 24x24 target finally reaches the 86
            // grouped reports. It shipped to the 33 flat ones on 2026-09-03 and could
            // not be applied here because that round was partitioned by FILE, so the
            // MAJORITY bucket kept the ~7-10px target for six days.
            // ⚠️ HANDLE_BOX_PX3, never HANDLE_BOX_PX4: this th is `px-3`, so the
            // previous column's z-10 resize strip reaches exactly the content-box edge
            // and _PX4's `-ml-[3px]` would bury 3 of the 24 px under a live
            // cursor-col-resize element — a target that measures 24 and behaves like
            // 21, unmeasurable in jsdom. The subtraction is in gridHeaderChrome.js.
            //
            // gap 4: text-rc-muted was text-gray-300 = #d1d5dc, 1.409:1 on this grid's
            // opaque bg-gray-50 thead. This span is a keyboard-operable role="button"
            // (useSortable's attributes) and dragging it is the only way to reorder a
            // column, so SC 1.4.11's 3.0:1 attaches to its resting glyph. Shipped at
            // the minimum compliant EXISTING token; the design tension (Himaad wanted
            // it LESS prominent) is written out in full at DataGrid.jsx's twin and is
            // an open question in spec 121 §4. Read it before lightening this.
            aria-label={`Reorder ${label} column`}
            className={`${HANDLE_BOX_PX3} cursor-grab text-rc-muted hover:text-rc-ink-2 text-[10px] leading-none`}
            title="Drag to reorder"
          >
            <span aria-hidden="true">⠿</span>
          </span>
        )}
        {children}
      </span>
      {/* Column resize handle — a hand-copied twin of DataGrid's DraggableHeader
          handle (different resize plumbing, same UX and same classes). Change one
          and change the other, or 86 grouped reports quietly get a different grid
          from the 33 flat ones — the split spec 092 exists to close (see the
          toolbar note below).

          B4 (2026-08-26): the mark used to be a 4px-tall, 1px-wide grey nub
          floating mid-cell that grew to full height on hover — it read as a
          smudge rather than a column boundary, and its 1px hit area was the
          smallest grab target in the grid. B4 made it a 1px hairline in
          --color-rc-control drawn by the ::after, with the <span> a transparent
          strip so the hit area is ~11x the visual; it sits inside the th's 12px
          right padding (px-3 here vs DataGrid's px-4), so it never overlays the
          sort button or the info icon. hover / focus / :active (:active =
          mid-drag, which is why this needs no isResizing state) thicken it to 2px
          in --color-rc-chip-text, the same blue as focus rings and is-active chips.

          Two things in the sentence B4 left here have since changed, and both are
          corrected rather than deleted:
            - it was FULL-HEIGHT and is now a 16px centred hairline (B1, Himaad
              2026-09-03: "it's not like cutting, it's like not edge to edge").
              spec 121 gap 1, this file, 2026-09-09.
            - it said "#8b95a5, 3.03:1 — it is the only thing marking the boundary,
              so it owes SC 1.4.11 its 3.0:1". The obligation is right; the number
              was measured against WHITE. On this grid's own bg-gray-50 thead
              (#f9fafb) it is 2.896:1, i.e. UNDER the floor it cites, by 3.5%. That
              is a DISCLOSED, pre-existing near-miss carried unchanged from
              origin/qa and unchanged by spec 121 — not compliance. It cannot be
              fixed from this line: a 3.0:1 mark on #f9fafb may carry at most
              L = 0.285 and #8b95a5 is L = 0.2970 (104.2% of the budget), so no
              colour clears the floor in this position. The only lever is the header
              FILL, which is a cross-app design decision for Himaad and Vlad. Never
              describe this mark as compliant, and never as decoration (spec 116 §1
              reverted exactly that argument). spec 116 §6, spec 121 §4.

          D2 (Himaad, 2026-08-27): the hover-revealed GRIP is the aria-hidden child
          below, and it is byte-identical to DataGrid's copy. The full rationale is
          on the twin — why it is a child of the separator and not a sibling (two
          suites count separators per th), why it must not be focusable, why the
          group is scoped to the strip rather than the th, why the always-on
          hairline stays for touch, and why the reveal is hover / focus-VISIBLE /
          :active. `active:` doing the mid-drag work is the mechanism this file
          already documents one line up. The lozenge is a stand-in for the icon
          asset Himaad owes; swapping it does not move anything else. */}
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label} column`}
        aria-valuenow={width ? Math.round(width) : undefined}
        tabIndex={0}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onDoubleClick={onResetWidth}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 24 : 8
          // Bug #10a: baseline from the rendered width (like the mouse path)
          // so the first keystroke nudges instead of jumping to ~120px.
          const rendered = e.currentTarget.closest('th')?.getBoundingClientRect()?.width
          const cur = width || (rendered ? Math.round(rendered) : 120)
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            onResize(Math.max(40, cur - step))
          } else if (e.key === 'ArrowRight') {
            e.preventDefault()
            onResize(cur + step)
          } else if (e.key === 'Enter' || e.key === 'Home') {
            e.preventDefault()
            onResetWidth()
          }
        }}
        title="Drag to resize · double-click to auto-fit · arrow keys to adjust"
        /* Straddles the column edge — see the twin in DataGrid.jsx for why. Round 1
           made this an 11px strip sitting entirely LEFT of the line; it is now
           ±8px either side. z-10 so the overhang beats the next th at hit-testing;
           the overhang lands in that th's px-3 padding.

           ✅ spec 121 gap 1 (2026-09-09) — B1 reaches the 86 grouped reports.
           Before: `after:inset-y-0 after:w-px after:bg-rc-control` with
           `hover:/focus:/active:after:w-[2px]`, i.e. a FULL-HEIGHT hairline that
           thickened and recoloured but carried no `after:h-*` token in ANY state.
           Now: DIVIDER_BASE + DIVIDER_RESTING interpolated from
           gridHeaderChrome.js — a 16px centred 1px --color-rc-control hairline,
           byte-identical to the flat grid's because it is the SAME STRING — plus a
           24px / 2px / --color-rc-chip-text reveal on all three states.

           ⚠️ The `after:h-6` in each reveal is not cosmetic and must not be dropped.
           The hover-revealed grip below is `h-4 w-1`; a 4px bead over a 16px line
           covers it end to end, which is exactly the "permanent nub standing in for
           a boundary that is not drawn" spec 098's B4 removed. 24 against 16 leaves
           4px of rule above and below. So swapping `inset-y-0` for `h-4` WITHOUT
           lengthening the reveal would have shipped a new defect on 86 reports while
           looking like the mechanical fix spec 116 §4 called it.
           `w-[2px]` was also normalised to `w-0.5` — identical 2px, but one literal
           now covers both renderers so a single assertion can hold them.

           Base-plus-variant here rather than the flat grid's mutually-exclusive
           ternary, deliberately: this grid hand-rolls resize (`startDrag` +
           document listeners) and has no `getIsResizing()`, so `:active` does the
           mid-drag work. That is safe where the ternary was necessary — a
           `hover:after:h-6` compiles to `&:hover{&::after{…}}`, one class-level
           specificity ABOVE the unprefixed `after:h-4`, so it wins on specificity
           rather than on Tailwind's emission order. The trap spec 116 documents is
           two UNPREFIXED rivals in one attribute; there are none here. */
        className={`group/resize absolute top-0 h-full z-10 ${isLastHeader ? 'w-[13px] right-0 after:right-0' : 'w-[25px] -right-[12px] after:right-[12px]'} cursor-col-resize touch-none select-none bg-transparent focus:outline-none ${DIVIDER_BASE} ${DIVIDER_RESTING} hover:after:h-6 hover:after:w-0.5 hover:after:bg-rc-chip-text focus:after:h-6 focus:after:w-0.5 focus:after:bg-rc-chip-text active:after:h-6 active:after:w-0.5 active:after:bg-rc-chip-text`}
      >
        {/* D2's grip. Byte-identical to src/components/common/DataGrid.jsx's copy —
            change one, change the other, or the 86 grouped reports get a different
            divider from the 33 flat ones. Swap this span's body/background for
            Himaad's icon when he delivers it; the positioning and the reveal stay. */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 h-4 w-1 -translate-y-1/2 rounded-full bg-rc-chip-text opacity-0 transition-opacity ${isLastHeader ? 'right-0' : 'right-[11px]'} group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100 group-active/resize:opacity-100`}
        />
      </span>
    </th>
  )
}

// CC-49732: `initialPageState` restores a saved view's page state on mount
// ({ columnFilters, toggles }); `pageStateRef` publishes a getter for the
// current page state so the Views menu can capture it (exportViewRef pattern).
// `showZeroToggle` (CC-49906): pages whose report definition carries its OWN
// zero-balance filter (e.g. Trial Balance "Show zero balances") pass false so
// the generic grid toggle doesn't duplicate it.
//
// `statementMode` (spec 127 §4.1, default false): the accounting statements' mode.
// Groups (CB's sections) never collapse, headers never sort, and leaf rows that
// carry `tree` get a per-parent roll-up toggle in the `report.treeColumnId` cell,
// plus "Roll up all" / "Expand all". Everything it adds is gated on this prop —
// without it `tree` fields are ignored and the grid is byte-for-byte what the 86
// grouped reports had before (pinned by groupedExportView.test.js, I15).
const ReportGroupedGrid = ({ report, storageKey, exportViewRef, onFilteredChange, pageStateRef, initialPageState, showZeroToggle = true, showInactiveToggle = true, pageDisplayExtras, onResetPageDisplayExtras, headerRow, statementMode = false }) => {
  const stripActions = useReportStripActions()
  const prefs = usePreferencesStore()
  const initialToggles = initialPageState?.toggles || null
  const treeColumnId = statementMode && report?.treeColumnId != null ? String(report.treeColumnId) : null

  // CC-49341 / CC-49743: per-report currency-symbol toggle — TRI-STATE.
  // null = auto (server display untouched; the checkbox mirrors whether the
  // data already shows $), true = force $ onto amounts, false = strip it.
  const [forceCurrency, setForceCurrency] = useState(
    () => initialToggles?.forceCurrency ?? (storageKey ? prefs.getCurrencySymbol?.(storageKey) : null) ?? GRID_DISPLAY_DEFAULTS.forceCurrency
  )
  useEffect(() => {
    // Only an explicit user choice persists — auto stays auto.
    if (storageKey && forceCurrency !== null) prefs.setCurrencySymbol?.(storageKey, forceCurrency)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCurrency, storageKey])
  // Spec 212: "Show commas in numbers", per report. ON (spec 211's separators) is
  // the default; only the user's own click is stored (THOUSANDS_PAGE_FLAG), so a
  // saved view's seed never overwrites their per-report choice.
  const [showThousands, setShowThousands] = useState(
    () => initialToggles?.showThousands ?? (storageKey ? prefs.getPageFlag?.(storageKey, THOUSANDS_PAGE_FLAG) : null) ?? GRID_DISPLAY_DEFAULTS.showThousands
  )

  // CC-50291: visible columns, with the firm's own name applied to any column
  // that opted in by carrying `aliasKey`, and that set published up to
  // `SpecializedReportShell` so an admin can rename them. Inert on the generic
  // report path and inside CsvReportGrid / AutoReportGrid, whose columns carry
  // no `aliasKey` — see `aliasedGridColumns.js` for why that opt-in exists.
  const baseColumns = useAliasedGridColumns(report)

  // Column visibility — persisted per report (same mechanism as the flat grid).
  const [hidden, setHidden] = useState(() => {
    const stored = storageKey ? prefs.getColumnVisibility(storageKey) : {}
    return stored || {} // maps columnId -> false when hidden
  })
  const isHidden = (col) => hidden[String(col.columnId)] === false

  // Column ORDER (CC-49459) — persisted per report like the flat grid. Stored
  // order is validated against current ids; stale ids drop, new ones append.
  const defaultOrder = useMemo(() => baseColumns.map((c) => String(c.columnId)), [baseColumns])
  const [colOrder, setColOrder] = useState(() => {
    const stored = storageKey ? prefs.getColumnOrder?.(storageKey) : null
    if (!Array.isArray(stored) || stored.length === 0) return null
    return stored
  })
  const order = useMemo(() => {
    if (!colOrder) return defaultOrder
    const valid = colOrder.filter((id) => defaultOrder.includes(id))
    const missing = defaultOrder.filter((id) => !valid.includes(id))
    return [...valid, ...missing]
  }, [colOrder, defaultOrder])
  useEffect(() => {
    if (storageKey && colOrder) prefs.setColumnOrder?.(storageKey, colOrder)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colOrder, storageKey])

  // Column WIDTHS (CC-49459) — persisted per report (same store keys as the
  // flat grid).
  //
  // CC-53347 — WHY `table-fixed` ALONE WAS NOT ENOUGH, measured in Chrome 151.
  // This table was `min-w-full` + a conditional `table-fixed`. `min-w-full` is
  // `min-width:100%` and says nothing about `width`, so the table's width stayed
  // AUTO — and with `width:auto` + `table-layout:fixed`, once the table's intrinsic
  // content width exceeds its container, Chromium runs the AUTOMATIC algorithm and
  // discards every inline `width` on a `th` outright. It is not clamped, it is
  // ignored: an 800px request is discarded as completely as a 200px one. Meanwhile
  // `getComputedStyle(table).tableLayout` still reports `"fixed"`, which is why
  // this looked impossible to diagnose.
  //
  // Differential proof (600px container, content overflowing to ~1900px, only the
  // table's own width varying):
  //   min-width:100% + fixed, col0 asks 200px -> renders 185.7px  (request ignored)
  //   min-width:100%, layout AUTO             -> renders 185.7px  (byte-identical)
  //   width:100%     + fixed, col0 asks 200px -> renders 200px    (honoured)
  // The 33 flat reports never had this because DataGrid.jsx uses `w-full`, i.e. a
  // DEFINITE width, so fixed layout genuinely runs there.
  //
  // AND SEEDING ALONE IS INERT — this is the half that is easy to get wrong.
  // Putting a width on every `th`, or on every `th` AND `td`, while the table stays
  // `min-w-full` renders byte-identically to the broken case. The table's width
  // property is the discriminator, not per-cell coverage. So the fix is both:
  // `w-full` (below) AND a seed (here).
  const [widths, setWidths] = useState(() => (storageKey && prefs.getColumnWidths?.(storageKey)) || {})
  // Mirrors `widths` for the callbacks below, which must not close over a stale
  // copy: a drag fires setColWidth on every mousemove.
  const widthsRef = useRef(widths)
  useEffect(() => {
    widthsRef.current = widths
    if (storageKey) prefs.setColumnWidths?.(storageKey, widths)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widths, storageKey])
  const tableRef = useRef(null)

  // The rendered width of every header cell, keyed by column id.
  //
  // Needed because a definite table width means unsized columns stop being
  // content-sized: under fixed layout they divide the remainder EQUALLY. Measured
  // — with only col0 carrying a width, five columns render 200/100/100/100/100.
  // On a 12-column financial statement that would re-lay-out eleven columns the
  // instant a user nudged one, which is a worse bug than the one being fixed. So
  // the first resize freezes what is already on screen and only then applies the
  // delta: proportions stay exactly as the user sees them and one column moves.
  //
  // `getBoundingClientRect().width` is the border-box width, and Tailwind's
  // preflight sets `box-sizing: border-box` globally, so it is the same quantity
  // an inline `width` sets. Measuring the wrong box here would shift every column
  // by its padding on the first resize.
  const measureRenderedWidths = useCallback(() => {
    const table = tableRef.current
    if (!table) return {}
    const out = {}
    // The FIRST header row only — the second is the column-filter row, whose cells
    // are not the width authority.
    const headRow = table.querySelector('thead tr')
    for (const th of headRow ? headRow.querySelectorAll('th[data-col-id]') : []) {
      const id = th.getAttribute('data-col-id')
      const w = Math.round(th.getBoundingClientRect().width)
      if (id && w > 0) out[id] = w
    }
    return out
  }, [])

  // spec 094 — a resize drag fires this continuously; trackTransform coalesces
  // the burst into one event carrying a count.
  const setColWidth = useCallback((id, w) => {
    trackGridTransform(storageKey, 'column_resize', String(id))
    // Seed from the DOM only on the FIRST resize, and only from what is on screen
    // BEFORE this change lands. Measured outside the state updater on purpose: an
    // updater can be invoked twice (StrictMode) and the second call would measure
    // a table the first call had already resized.
    const seed = Object.keys(widthsRef.current).length === 0 ? measureRenderedWidths() : null
    setWidths((prev) => (Object.keys(prev).length === 0 && seed ? { ...seed, [id]: w } : { ...prev, [id]: w }))
  }, [storageKey, measureRenderedWidths])
  const resetColWidth = useCallback((id) => {
    trackGridTransform(storageKey, 'column_resize', 'reset')
    setWidths((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [storageKey])
  const anyWidths = Object.keys(widths).length > 0

  // Bug #10: coordinateGetter makes keyboard reorder actually move (WCAG 2.1.1);
  // the 5px activation constraint matches the flat grid so plain clicks on the
  // handle never start an accidental drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  // CC-49462: which column is frozen at the left edge — same rules and same
  // `pin:<columnId>` page-flag storage as the flat grid (src/utils/columnPinning.js),
  // so the two renderers can't drift. Resolved against `order` — the user's
  // CURRENT order — so the unset default freezes whatever is already leftmost
  // rather than overriding a persisted drag-reorder (CC-49459).
  // Scoped to the columns the user can SEE (EX7): a pin on a hidden column
  // renders no cell, so counting it would leave the toggle claiming "frozen"
  // over a grid that is not frozen.
  const isColumnVisible = useCallback((id) => hidden[String(id)] !== false, [hidden])
  const pinnedColumnId = useMemo(
    () =>
      storageKey
        ? resolvePinnedColumnId(order, (flag) => prefs.getPageFlag?.(storageKey, flag), isColumnVisible)
        : null,
    [storageKey, order, prefs, isColumnVisible]
  )
  // "First column" for the Freeze button = the leftmost VISIBLE column, so the
  // button can never write a pin the resolver will refuse.
  const pinTargetId = useMemo(() => pinToggleTargetId(order, isColumnVisible), [order, isColumnVisible])
  const togglePin = useCallback(() => {
    if (!storageKey) return
    const readFlag = (flag) => prefs.getPageFlag?.(storageKey, flag)
    // Emitted here, not around setPageFlag: unfreeze writes one flag per column.
    trackGridTransform(storageKey, 'column_pin', pinnedColumnId ? 'unfreeze' : 'freeze')
    if (pinnedColumnId) {
      // Any pin still stored against a hidden column is cleared with it, or
      // showing that column again would re-freeze the grid on its own.
      for (const id of pinFlagsToClearOnUnfreeze(order, readFlag, pinnedColumnId)) {
        prefs.setPageFlag?.(storageKey, pinFlagName(id), false)
      }
      return
    }
    if (pinTargetId) prefs.setPageFlag?.(storageKey, pinFlagName(pinTargetId), true)
  }, [storageKey, pinnedColumnId, pinTargetId, order, prefs])
  const scrollRef = usePinnedScrollShadow(!!pinnedColumnId)
  const isPinnedCol = useCallback(
    (col) => !!pinnedColumnId && String(col?.columnId) === pinnedColumnId,
    [pinnedColumnId]
  )

  const onDragEnd = useCallback((event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    // The frozen column is neither draggable nor a drop target (CC-49462).
    if (pinnedColumnId && (String(active.id) === pinnedColumnId || String(over.id) === pinnedColumnId)) return
    // After the early returns, so a drag that changed nothing is not counted.
    trackGridTransform(storageKey, 'column_reorder', String(active.id))
    setColOrder((prev) => {
      const cur = (() => {
        if (!prev) return defaultOrder
        const valid = prev.filter((id) => defaultOrder.includes(id))
        const missing = defaultOrder.filter((id) => !valid.includes(id))
        return [...valid, ...missing]
      })()
      // Reorder against what the user can actually see — the pin-hoisted order —
      // then store that, so the stored and rendered orders stay in agreement.
      const seen = orderWithPinFirst(cur, pinnedColumnId)
      const oldIndex = seen.indexOf(String(active.id))
      const newIndex = seen.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(seen, oldIndex, newIndex)
    })
  }, [defaultOrder, pinnedColumnId, storageKey])

  const visibleColumns = useMemo(() => {
    const byId = new Map(baseColumns.map((c) => [String(c.columnId), c]))
    // CC-49462: the frozen column renders at the left edge regardless of where
    // it sits in the user's order.
    return orderWithPinFirst(order, pinnedColumnId).map((id) => byId.get(id)).filter((c) => c && !isHidden(c))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseColumns, hidden, order, pinnedColumnId])
  const toggleColumn = useCallback((col) => {
    trackGridTransform(storageKey, 'column_hide', String(col?.columnId))
    setHidden((prev) => {
      const id = String(col.columnId)
      const next = { ...prev, [id]: prev[id] === false ? true : false }
      if (storageKey) prefs.setColumnVisibility(storageKey, next)
      return next
    })
  }, [prefs, storageKey])

  // Bug #12: grouped sort persists per report (like widths/order) so named
  // views can capture/restore it. CC-49331: `sorts` is an ARRAY of levels —
  // plain click replaces the sort; shift+click adds/advances a secondary (then
  // tertiary) level, matching the flat grid's react-table multi-sort.
  // CC-49329: with nothing persisted, CB's column metadata default
  // (sortDirectionName ASC/DESC) seeds the initial sort.
  //
  // Spec 127 S3: a statement is never sorted. Its order IS the statement (an
  // account, its sub-accounts, its Total line), and one header click used to
  // scramble it for good: `report:pnl:std` is shared by P&L AND Comparative, so a
  // persisted sort re-sorted both pages on every later visit (verifier 1). In
  // statement mode a persisted sort is IGNORED, not deleted — the stored value is
  // left for the day the page stops being a statement.
  const [sorts, setSorts] = useState(() => {
    if (statementMode) return []
    const stored = storageKey ? prefs.getSorting?.(storageKey) : null
    if (Array.isArray(stored) && stored.length > 0 && stored[0]?.id) {
      return stored.map((x) => ({ id: String(x.id), desc: !!x.desc }))
    }
    // CC-49948: V1's narrower rule — non-groupable columns only, by SortOrder.
    // A groupable column's SortDirection orders its GROUPS, not the rows.
    return metadataDefaultSort(baseColumns)
  })
  const sort = sorts[0] || null // primary level, for callers that only care about it
  // CC-49948: persist only a sort the USER changed — see the matching comment in
  // DataGrid. Writing on mount stored the metadata default as a user choice, so
  // the stored value shadowed the metadata rule on every later visit (Revenue by
  // State kept its pre-fix State-ascending sort even after the fix shipped).
  const sortsPersistedOnce = useRef(false)
  useEffect(() => {
    if (!sortsPersistedOnce.current) {
      sortsPersistedOnce.current = true
      return
    }
    // Statement mode never persists (a reset's setSorts([]) would otherwise
    // overwrite the ignored-not-deleted value above).
    if (storageKey && !statementMode) prefs.setSorting?.(storageKey, sorts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorts, storageKey])
  const toggleSort = useCallback((col, event) => {
    const id = String(col.columnId)
    const additive = !!event?.shiftKey // CC-49331: shift+click = add a level
    // spec 094 — the user's CLICK, so CB's column-metadata sort default never
    // reads as a choice. That distinction is the whole point of the
    // `sortsPersistedOnce` mount guard below (CC-49948).
    trackGridTransform(storageKey, 'sort', additive ? 'multi' : 'single')
    setSorts((prev) => {
      const idx = prev.findIndex((x) => x.id === id)
      if (!additive) {
        // Plain click: cycle asc → desc → off, replacing all levels.
        if (idx !== 0 || prev.length === 0) return [{ id, desc: false }]
        if (!prev[0].desc) return [{ id, desc: true }]
        return []
      }
      // Shift+click: cycle this column WITHIN the existing levels (max 3).
      if (idx === -1) return prev.length >= 3 ? prev : [...prev, { id, desc: false }]
      const cur = prev[idx]
      if (!cur.desc) return prev.map((x, i) => (i === idx ? { id, desc: true } : x))
      return prev.filter((_, i) => i !== idx)
    })
  }, [storageKey])

  // { columnId: text } — seeded from a restored saved view (CC-49732).
  const [filters, setFilters] = useState(() => initialPageState?.columnFilters || {})
  const setFilter = useCallback((col, text) => {
    // The typed TEXT is deliberately not sent: on a report grid it is a client,
    // matter or timekeeper name. Only the column id, and only that a
    // client-side filter was used at all.
    trackGridTransform(storageKey, 'client_col_filter', String(col?.columnId))
    setFilters((prev) => ({ ...prev, [String(col.columnId)]: text }))
  }, [storageKey])
  // Spec 171: moves on "Reset grid layout" (handleReset), so an operator chosen
  // in the filter row goes back to Contains with the filters it cleared. The
  // operator itself rides INSIDE each filter value (`{ text, mode }`), so this
  // `filters` map — and every saved view of it — needs no new key.
  const [filterModeResetKey, setFilterModeResetKey] = useState(0)

  const [collapsed, setCollapsed] = useState({})
  // CC-49905: Expand all / Collapse all (V1's "Expand All" option on P&L and
  // every other grouped report). The per-group map OVERRIDES this default, so
  // "Collapse all" works without enumerating every group key up front.
  const [collapseDefault, setCollapseDefault] = useState(false)
  const toggleCollapse = useCallback((key) => {
    // The group KEY is never sent — it is built from group header content, i.e.
    // a client or matter name. Only the direction goes out.
    trackGridTransform(storageKey, 'group_collapse', 'one')
    setCollapsed((prev) => ({ ...prev, [key]: !(prev[key] ?? collapseDefault) }))
  }, [collapseDefault, storageKey])
  const setAllCollapsed = useCallback((on) => {
    trackGridTransform(storageKey, on ? 'group_collapse' : 'group_expand_all', 'all')
    setCollapseDefault(on)
    setCollapsed({})
  }, [storageKey])
  // Spec 127 S1: a statement's sections are never collapsible — Robin "never
  // wants" Income to collapse as a whole; the collapsible unit is a PARENT
  // ACCOUNT (the roll-up below). `collapsed` / `collapseDefault` are never
  // consulted in statement mode, so every group renders expanded and the export
  // view reports `collapsed: false`.
  const isGroupCollapsed = (key) => (statementMode ? false : (collapsed[key] ?? collapseDefault))

  // Spec 127 S4: which parents are rolled up. Default empty = fully expanded
  // (Jordan's decision 1). Seeded from a saved view; ids the current run does not
  // have are ignored by buildStatementIndex, never an error.
  const [rolledIds, setRolledIds] = useState(
    () => new Set(statementMode && Array.isArray(initialPageState?.rolledIds) ? initialPageState.rolledIds : [])
  )
  const statementIndex = useMemo(
    () => (statementMode ? buildStatementIndex(report, rolledIds) : null),
    [statementMode, report, rolledIds]
  )
  // Tree handling only for a well-formed tree (duplicate ids refuse it, see the index).
  const tree = statementIndex?.valid && statementIndex.hasTree ? statementIndex : null
  const hasRollable = !!tree && tree.rollable.size > 0
  // S10: ids and names never leave the browser — only which control was used.
  const toggleRolled = useCallback((id) => {
    trackGridTransform(storageKey, 'row_rollup', 'one')
    setRolledIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [storageKey])
  // S2: "Roll up all" rolls EVERY rollable node at every depth (V1's unchecked
  // "Expand All"), so expanding one top-level parent later shows its own
  // sub-parents still rolled. "Expand all" clears the set.
  const rollUpAll = useCallback(() => {
    trackGridTransform(storageKey, 'row_rollup', 'all')
    setRolledIds(new Set(statementIndex?.rollable || []))
  }, [storageKey, statementIndex])
  const expandAllRolled = useCallback(() => {
    trackGridTransform(storageKey, 'row_rollup', 'expand_all')
    setRolledIds(new Set())
  }, [storageKey])

  // "Reset grid layout" (CC-49380) used to be defined here. It MOVED DOWN, to
  // just below the view-option state (hideZero / hideInactive / the aging
  // window), because E4 makes it reset those too and they are declared later in
  // this component. Search `handleReset` — it is one useCallback, in the same
  // hook order every render, wired to the same menu row.

  // Column-visibility dropdown — closes on click-outside and Escape (a11y).
  const [showColumns, setShowColumns] = useState(false)
  // Per-column filters collapse by default (de-clutter the header). Reveal on the
  // toolbar toggle OR whenever a filter is actually active (e.g. from a saved view)
  // so applied filters are never hidden.
  const [showFilters, setShowFilters] = useState(false)
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v != null && String(v).trim() !== '').length,
    [filters]
  )
  const filtersVisible = showFilters || activeFilterCount > 0
  const columnsRef = useRef(null)
  useEffect(() => {
    if (!showColumns) return undefined
    const onDown = (e) => {
      if (columnsRef.current && !columnsRef.current.contains(e.target)) setShowColumns(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setShowColumns(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [showColumns])

  // Totals recompute toggle (CC-49382). CC-49456: defaults ON so subtotals + the
  // grand total follow the filter by default (the usual intent); user can uncheck
  // to see the full unfiltered server totals. SUM-only guardrail unchanged.
  const [recompute, setRecompute] = useState(initialToggles?.recompute ?? true)

  // Row-exclusion toggles (CC-49318 $0 rows, CC-49319 inactive/closed). The
  // inactive toggle is only offered when the report carries a status-like column.
  const numericColIds = useMemo(
    () => baseColumns.filter(isNumericColumn).map((c) => String(c.columnId)),
    [baseColumns]
  )
  // Spec 133: columns CB declares DECIMAL / INTEGER. Hide $0 never hides a row
  // holding a non-zero value in one of them, even where CB left-aligned it.
  const amountColIds = useMemo(() => declaredAmountColumnIds(baseColumns), [baseColumns])
  // CC-49944: what each column's rows actually hold (numeric / text / empty), so
  // the header tooltip states a type the data supports instead of guessing from
  // alignment alone.
  const dataKinds = useMemo(() => columnDataKinds(report), [report])
  // CC-49944: which columns the report actually totals (report footer OR any
  // group subtotal), so a tooltip never promises a total that isn't rendered.
  const totaledIds = useMemo(() => totaledColumnIds(report), [report])
  // CC-49944 (QA test 7): a formatted example value per column for the tooltip.
  const samples = useMemo(() => columnSamples(report, dataKinds), [report, dataKinds])
  // CC-53905: which columns' own source already marks them as currency — the
  // only Show $ signal an adapter column (no CB CellsFormat) has.
  const currencyMarked = useMemo(() => currencyMarkedColumnIds(report), [report])
  // CC-53905 review fix 4: the columns CB totals with a number. With the metadata
  // rule they make `showDollarNumeric`, the ONE numeric test every Show $ site
  // uses — this grid's cells, subtotals, totals and toggle, and
  // buildGroupedExportView — so the row is offered wherever a saved choice acts,
  // and the screen and the files put the "$" on the same columns.
  const numericTotals = useMemo(() => numericTotalColumnIds(report), [report])
  // CC-53905: Show $ is offered only where it can change something (some numeric
  // column takes a "$" under ON or shows one OFF would strip). The export view
  // applies the same test before it publishes an explicit choice
  // (buildGroupedExportView's `currencyOverride`).
  const currencyActs = useMemo(() => currencyToggleActs(baseColumns, currencyMarked, numericTotals), [baseColumns, currencyMarked, numericTotals])
  // Spec 212: offered only where some figure is 1,000+, as Show $ is only where it acts.
  const thousandsActs = useMemo(() => thousandsToggleActs(report, baseColumns, numericTotals), [report, baseColumns, numericTotals])
  const statusCol = useMemo(() => findStatusColumn(baseColumns), [baseColumns])
  const statusColId = statusCol ? String(statusCol.columnId) : null
  // Only offer the inactive/closed toggle when the status column ACTUALLY holds
  // inactive/closed values (bug #4) — not for any "*Status" column (Budget/Fee/etc).
  const hasInactiveRows = useMemo(
    () => !!statusColId && collectLeaves(report?.reportData || []).some(
      (lf) => isInactiveStatusValue(cellById(lf.dataCells, statusColId)?.displayValue ?? '')
    ),
    [statusColId, report]
  )
  const [hideZero, setHideZero] = useState(!!(initialToggles?.hideZero ?? GRID_DISPLAY_DEFAULTS.hideZero))
  const [hideInactive, setHideInactive] = useState(!!(initialToggles?.hideInactive ?? GRID_DISPLAY_DEFAULTS.hideInactive))

  // CC-49743: in auto mode the Show-$ checkbox mirrors what the data actually
  // shows, so unchecking it on a $-formatted report (e.g. P&L) strips the $.
  // CC-53905 review fix 4: over the columns Show $ acts on (`showDollarNumeric`).
  const serverShowsCurrency = useMemo(() => {
    const numericIds = baseColumns.filter((c) => showDollarNumeric(c, numericTotals)).map((c) => String(c.columnId))
    return collectLeaves(report?.reportData || []).some((lf) =>
      numericIds.some((id) => displayHasCurrency(cellById(lf.dataCells, id)?.displayValue))
    )
  }, [baseColumns, report, numericTotals])

  // Aging / inactivity window (CC-49327) — same control as the flat grid,
  // applied to leaf rows (group subtotals recompute per the CC-49456 flow).
  const agingCol = useMemo(() => findAgingColumn(baseColumns), [baseColumns])
  const agingColId = agingCol ? String(agingCol.columnId) : null
  const hasAgingDates = useMemo(
    () => !!agingColId && collectLeaves(report?.reportData || []).some(
      (lf) => parseCellDate(cellById(lf.dataCells, agingColId)?.displayValue ?? '') != null
    ),
    [agingColId, report]
  )
  const [agingMode, setAgingMode] = useState(initialToggles?.agingMode || GRID_DISPLAY_DEFAULTS.agingMode)
  const [customDays, setCustomDays] = useState(initialToggles?.customDays || GRID_DISPLAY_DEFAULTS.customDays)

  /*
   * Reset this report's grid to default (CC-49380): clears persisted visibility
   * + resets in-memory sort / filter / collapse / widths / order.
   *
   * E4 (2026-08-27) — AND the four View options in the same menu. Until now this
   * reset stopped one divider short of them: a user ticked "Hide $0 /
   * zero-balance rows", clicked the reset two rows below it, and kept hiding
   * rows on a grouped WIP or A/R report while believing the grid was back to
   * default. On a financial grid that is a wrong number, not a stale checkbox —
   * every subtotal and the grand total recompute over the visible rows
   * (CC-49456), so the hidden rows are missing from the money as well as the
   * screen.
   *
   * Declared HERE, below those four states, rather than up with the other
   * layout callbacks: referencing a `const` from a closure defined above it
   * would work (the click is long after render), but reading the reset before
   * knowing what it resets is how the four got missed in the first place.
   *
   * `recompute` ("Total just the filtered rows") is deliberately NOT reset: it
   * lives on the totals strip below the grid, not in this menu, and its default
   * is ON — resetting it while a filter survived elsewhere would change totals
   * the user did not ask to change.
   *
   * ~~`pageDisplayExtras` (Aged A/R's own practice-area control) is the page's
   * state, not this grid's, and is likewise untouched.~~ SUPERSEDED 2026-08-28.
   * The premise survives — the state IS the page's and this grid still cannot
   * reach it — but the conclusion was wrong, and it understated the stake twice
   * over.
   *
   * "Aged A/R's own practice-area control" describes a select. Aged A/R is the
   * only page that supplies this slot, and it puts a practice-area select AND
   * TWO ROW-HIDING CHECKBOXES in it — "Hide zero balances" (DEFAULTING ON,
   * ARReport.jsx `useState(true)`) and "Hide inactive/closed matters". AR also
   * passes `showZeroToggle={false} showInactiveToggle={false}`, so on that page
   * those two are the ONLY row-hiding controls in this menu: resetting this
   * grid's own hideZero / hideInactive reset a SHADOW COPY that is suppressed
   * from the UI. The reset looked like it did something and changed nothing the
   * user could see, on the report CLAUDE.md names as the specialized baseline.
   *
   * "Belongs to the page" is a reason to ASK the page, not to skip it. The page
   * hands down `onResetPageDisplayExtras` beside `pageDisplayExtras` — the same
   * seam ReportDataGrid uses to hand `onResetDisplayOptions` down to DataGrid —
   * and it is called at the end of this reset. A page that supplies the controls
   * and NOT the hook is no longer silently ignored: `displayToggles` below
   * renders PAGE_EXTRAS_NOT_RESET_NOTE in the menu so the omission is visible.
   *
   * The pin unfreezes for free and still does: it is a `pin:<columnId>` entry in
   * the store's generic `pageFlags` map and `resetPagePreferences` deletes
   * `pageFlags[page]` wholesale (src/store/preferencesStore.js:157, re-verified
   * 2026-08-28; :155 of the same block drops the stored currency symbol).
   */
  const handleReset = useCallback(() => {
    trackGridTransform(storageKey, 'reset_layout')
    if (storageKey) prefs.resetPagePreferences(storageKey)
    setHidden({})
    setSorts([])
    setFilters({})
    setFilterModeResetKey((k) => k + 1) // spec 171: operators back to Contains
    setCollapsed({})
    setCollapseDefault(false)
    // Spec 127 S9: a reset re-expands every rolled-up parent.
    setRolledIds(new Set())
    setWidths({})
    setColOrder(null)
    // View options. `null` (not false) for the currency toggle — it is TRI-state
    // and null is "auto / whatever the server sent", which is the default. false
    // would mean "actively strip the $", i.e. a different override, not a reset.
    // resetPagePreferences has already dropped the stored symbol, and the
    // persist effect skips null, so the two agree afterwards.
    // The five values come from GRID_DISPLAY_DEFAULTS, not from literals, and
    // NOT from `initialToggles` — see that module for why the divergence from a
    // saved view's seed is deliberate and must stay row-REVEALING.
    setForceCurrency(GRID_DISPLAY_DEFAULTS.forceCurrency)
    // Spec 212: resetPagePreferences has already deleted the stored flag.
    setShowThousands(GRID_DISPLAY_DEFAULTS.showThousands)
    setHideZero(GRID_DISPLAY_DEFAULTS.hideZero)
    setHideInactive(GRID_DISPLAY_DEFAULTS.hideInactive)
    setAgingMode(GRID_DISPLAY_DEFAULTS.agingMode)
    setCustomDays(GRID_DISPLAY_DEFAULTS.customDays)
    // LAST, and via the page, because this grid does not own that state.
    onResetPageDisplayExtras?.()
  }, [storageKey, prefs, onResetPageDisplayExtras])

  // Publish the current page state for the Views menu (CC-49732) — same
  // lifecycle as exportViewRef, refreshed every render so it's never stale.
  // Grouped grids keep their native { columnId: text } filter shape.
  //
  // Spec 127 S9: in statement mode the rolled set rides along as `rolledIds` —
  // the parents THIS run actually has rolled, in CB order. Off statement mode the
  // key is absent, so every other page's saved-view payload is byte-identical to
  // before (I8's negative assertion).
  useEffect(() => {
    if (!pageStateRef) return
    pageStateRef.current = () => ({
      columnFilters: filters,
      toggles: { forceCurrency, showThousands, hideZero, hideInactive, agingMode, customDays, recompute },
      ...(statementMode ? { rolledIds: tree ? [...tree.rolledInOrder] : [] } : {}),
    })
  })

  // CC-49843: tell a surrounding SpecializedReportShell that this report HAS
  // groups, so it can offer the "PDF: summary only" scope. The shell's checkbox
  // was gated on a `hasGroups` prop that ONLY ReportView and ARReport ever
  // passed, so on every shell-based grouped page (Balance Sheet, General Ledger,
  // GL Budget, Calendar, P&L, grouped AP/Time Entries) the control was built but
  // unreachable — QA reported it as "no summary only option exists anywhere".
  // Reporting it here rather than from the nine wrapper components keeps one
  // source of truth: the grid that actually knows. Metadata only — the wrappers
  // still own `ctx.ref`, so the two publishers never conflict. No context (the
  // generic ReportView path) makes this a no-op.
  const shellExportCtx = useReportExportView()
  const reportHasGroups = useMemo(
    () => (report?.reportData || []).some((r) => r.isGroup),
    [report]
  )
  useEffect(() => {
    shellExportCtx?.onPublish?.({ hasGroups: reportHasGroups })
  }, [shellExportCtx, reportHasGroups])

  const staleDays = useMemo(() => {
    if (agingMode === 'custom') {
      const n = parseInt(customDays, 10)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    return agingMode ? parseInt(agingMode, 10) : null
  }, [agingMode, customDays])
  const agingActive = hasAgingDates && staleDays != null

  const togglesActive =
    (hideZero && numericColIds.length > 0) || (hideInactive && hasInactiveRows) || agingActive

  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([, v]) => filterIsActive(v)),
    [filters]
  )
  // A leaf is hidden by any active column filter OR an active row-exclusion toggle;
  // either makes the report "filtered" so subtotals/totals recompute (CC-49456).
  const anyRowFilter = activeFilters.length > 0 || togglesActive
  const isFiltered = anyRowFilter

  // CC-49354 #3: surface the filtered state so ReportView can warn on the export
  // control when the grid is filtered but the export scope boxes are unchecked.
  useEffect(() => {
    onFilteredChange?.(isFiltered)
  }, [isFiltered, onFilteredChange])

  // Forgiving per-column filter (shared with the flat grid) + the row-exclusion
  // toggles — a leaf must clear all of them.
  //
  // `passesOther` is every test EXCEPT Hide $0, so statement mode can swap in the
  // tree-aware zero rule below for tree rows (spec 127 S6) while column filters,
  // inactive and aging stay per row. Off statement mode `rowPasses` is exactly the
  // rule it always was.
  const passesOther = useCallback((row) => {
    if (!activeFilters.every(([cid, v]) => matchColumnFilter(cellById(row.dataCells, cid)?.displayValue, v))) return false
    if (hideInactive && statusColId &&
        isInactiveStatusValue(cellById(row.dataCells, statusColId)?.displayValue ?? '')) return false
    if (agingActive && agingColId &&
        !isStaleValue(cellById(row.dataCells, agingColId)?.displayValue ?? '', staleDays)) return false
    return true
  }, [activeFilters, hideInactive, statusColId, agingActive, agingColId, staleDays])
  // S6: a parent with an own $0.00 stays while any sub-account is visible; a
  // rolled parent stays when its rolled figures OR its expanded subtree would
  // (rolling never removes the row — or the toggle just pressed); a Total line
  // follows its parent.
  const treeZero = useMemo(() => {
    if (!tree || !(hideZero && numericColIds.length > 0)) return null
    const isZeroCells = (cells) => isHideableZeroRow(numericColIds, amountColIds, (id) => cellNumber(cellById(cells, id)))
    return treeZeroKeep(tree, { isZeroCells, passesOther })
  }, [tree, hideZero, numericColIds, amountColIds, passesOther])
  const rowPasses = useCallback((row) => {
    if (!passesOther(row)) return false
    if (hideZero && numericColIds.length > 0) {
      if (treeZero && tree.treeOf(row)) {
        if (!treeZero(row)) return false
      } else if (isHideableZeroRow(numericColIds, amountColIds, (id) => cellNumber(cellById(row.dataCells, id)))) {
        return false
      }
    }
    return true
  }, [passesOther, hideZero, numericColIds, amountColIds, treeZero, tree])

  const groupHasMatches = useCallback(function check(rows) {
    return (rows || []).some((r) => (r.isGroup ? check(r.dataRows) : rowPasses(r)))
  }, [rowPasses])

  const processLeafRows = useCallback((rowsArg) => {
    let out = rowsArg.filter((r) => !r.isGroup)
    if (anyRowFilter) out = out.filter(rowPasses)
    // Spec 127 S5: roll-up is a PRESENTATION swap, not a filter — it never
    // touches `anyRowFilter` / `isFiltered`, so no "· filtered" badge, no totals
    // bar and no subtotal recompute follow from it (I9). Rows a rolled parent
    // hides simply are not drawn.
    if (tree) out = out.filter((r) => !tree.rollupHidden(r))
    // S3: a statement is never sorted (`sorts` is always [] there; the guard
    // makes that a property of this function, not of the state's history).
    if (sorts.length > 0 && !statementMode) {
      // CC-54054 (spec 141): a leaf the page marks `pin: 'first'` or `pin: 'last'`
      // is a balance line, not data to order — a General Ledger account's
      // Beginning Balance and its Totals. Only the rows between them sort; pinned
      // rows keep the page's order at their own end. No pin, no change.
      const pinnedFirst = out.filter((r) => r.pin === 'first')
      const pinnedLast = out.filter((r) => r.pin === 'last')
      const sortable = out.filter((r) => r.pin !== 'first' && r.pin !== 'last')
      // CC-49331: compare level by level — primary, then secondary, then tertiary.
      sortable.sort((ra, rb) => {
        for (const lvl of sorts) {
          const r = compare(sortKey(cellById(ra.dataCells, lvl.id)), sortKey(cellById(rb.dataCells, lvl.id)))
          if (r !== 0) return lvl.desc ? -r : r
        }
        return 0
      })
      out = [...pinnedFirst, ...sortable, ...pinnedLast]
    }
    return out
  }, [anyRowFilter, rowPasses, sorts, tree, statementMode])

  // Memoized so their identity is stable across renders (they feed the `summable`
  // useMemo below; inline `|| []` arrays would retrigger it every render).
  const rows = useMemo(() => report?.reportData || [], [report])
  const footerCells = useMemo(() => report?.reportFooterCells || [], [report])
  // CC-49860: reports that close with several total rows publish them as
  // `reportFooterRows` (array of cell arrays). Everything else keeps the single
  // `reportFooterCells` row, so existing callers are unaffected.
  // (Spec 127 S3: the rule lives in groupedExportView.reportFooterLines.)
  const footerRows = useMemo(() => reportFooterLines(report), [report])

  // ---- CC-49616: display pagination over TOP-LEVEL groups -------------------
  // Grouped reports with thousands of rows rendered everything at once and
  // froze the browser (2,422 rows in the field). Only the current page of
  // groups renders; the EXPORT view still publishes every row (exports must
  // never silently truncate), the grand total keeps describing the full
  // dataset, and printing bypasses paging so paper gets everything.
  // Pref read optional-chained like the rest of the store usage; falls back
  // to a 25-groups page.
  const groupPageSize = (storageKey && prefs.getGroupPageSize?.(storageKey)) || 25
  const [pageIndex, setPageIndex] = useState(0)
  const [printing, setPrinting] = useState(false)
  useEffect(() => {
    // beforeprint fires for Ctrl+P and the toolbar's window.print(); flushSync
    // so the full DOM exists before the dialog snapshots the page (the print
    // CSS already unlocks the scroll containers).
    const on = () => flushSync(() => setPrinting(true))
    const off = () => setPrinting(false)
    window.addEventListener('beforeprint', on)
    window.addEventListener('afterprint', off)
    return () => {
      window.removeEventListener('beforeprint', on)
      window.removeEventListener('afterprint', off)
    }
  }, [])
  // Pages count the top-level units a filter leaves visible, so sparse filter
  // results don't strand the user on empty pages.
  const visibleTop = useMemo(() => {
    if (!(activeFilters.length || agingActive)) return rows
    return rows.filter((r) => !r.isGroup || groupHasMatches(r.dataRows))
  }, [rows, activeFilters, agingActive, groupHasMatches])
  // Spec 213: a page also closes at GROUP_PAGE_ROW_BUDGET rendered rows (see
  // groupPages.js). Report 49 on All Dates put 12,550 rows on page 1, because its
  // 25 originators were each enormous; a page that fits both rules is unchanged.
  const pageStarts = useMemo(() => groupPageStarts(visibleTop, groupPageSize), [visibleTop, groupPageSize])
  const pageCount = pageStarts.length
  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1)
  }, [pageIndex, pageCount])
  const paging = !printing && pageCount > 1
  const pageNumber = Math.min(pageIndex, pageCount - 1)
  const pageStart = pageStarts[pageNumber]
  const pageEnd = pageStarts[pageNumber + 1] ?? visibleTop.length
  // When not paging, keep passing the ORIGINAL rows so the existing in-render
  // group-hiding path (and its keys) behave exactly as before.
  const pageRows = paging ? visibleTop.slice(pageStart, pageEnd) : rows
  const pageGroupOffset = paging ? visibleTop.slice(0, pageStart).filter((r) => r.isGroup).length : 0

  // SUM-only classification (CC-49382): a column is safe to recompute only if its
  // server GRAND total already equals the sum of every leaf row — i.e. it's a true
  // sum, not an average/rate/weighted figure.
  // Declared footer aggregates (CC-49302) make the SUM-only guardrail
  // authoritative. (Spec 127 S3: the rule lives in groupedExportView so the
  // export view and the screen classify with one function.)
  const summable = useMemo(() => computeSummable(report, baseColumns), [report, baseColumns])

  // CC-49317: distinct values per name-type column feed the per-column value picker.
  const pickerValuesByCol = useMemo(() => {
    const leaves = collectLeaves(rows)
    const out = {}
    for (const col of baseColumns) {
      if (isNameColumn(col)) {
        const id = String(col.columnId)
        out[id] = distinctDisplayValues(leaves.map((lf) => cellById(lf.dataCells, id)?.displayValue ?? ''))
      }
    }
    return out
  }, [rows, baseColumns])

  // Publish the current view for the export control (CC-49370): every column with
  // visibility + the user's drag order (CC-49459), and every leaf row (visible =
  // passes the active filters), values as plain text.
  //
  // Spec 127 S3: the builder MOVED, unchanged in output, to
  // groupedExportView.buildGroupedExportView — every pre-existing field is pinned
  // against views recorded before the move (groupedExportView.test.js, I15). This
  // effect only hands it what the grid knows. Why each input matters is written
  // there; the one that bites is `collapseBase`: collapse keys are POSITIONAL, and
  // `pageRows` is the raw `rows` when not paging and a `visibleTop` slice when
  // paging (level-0 indices offset by `pageGroupOffset`), so walking the whole of
  // that same base list from index 0 reproduces the renderer's keys (CC-49843).
  useEffect(() => {
    if (!exportViewRef) return
    exportViewRef.current = () => buildGroupedExportView(report, {
      columns: baseColumns,
      order,
      hidden,
      anyRowFilter,
      rowPasses,
      hideEmptyGroups: activeFilters.length > 0 || agingActive,
      collapseBase: paging ? visibleTop : rows,
      isCollapsed: isGroupCollapsed,
      summable,
      isFiltered,
      recompute,
      forceCurrency,
      showThousands,
      footerRows,
      statement: statementMode ? { rolledIds, treeColumnId } : null,
    })
  })

  if (!report) return null
  const colSpan = visibleColumns.length || 1

  // Footer cell (group subtotal or grand total) honoring the recompute toggle.
  // Recompute a column ONLY when it is a true plain sum FOR THIS SCOPE — i.e. this
  // scope's server total equals the sum of ALL its own leaves (bug #2). That stops
  // balance-style columns (running/net totals, e.g. Trust Balance, where a group's
  // subtotal isn't the sum of its rows) from being re-summed to a wrong figure;
  // those keep the server value with the "i" marker. `allLeaves` = this scope's
  // full leaf set (group's leaves for a subtotal; every leaf for the grand total).
  // (Spec 127 S3: the SUM-safe decision is groupedExportView.footerRecompute, so
  // the export outline can never print a subtotal this cell does not show.)
  // CC-49743: subtotal/grand-total cells follow the Show-$ toggle like data
  // cells (explicit ON adds, explicit OFF strips, auto passes through).
  // Spec 196 (CC-54309): EVERY line of a footer block takes it, not just line 0 —
  // Timekeeper Analysis' YTD total kept "$139,510.46" under OFF while every other
  // "$" left the grid. Lines 1..N still print CB's own digits; only the symbol moves.
  // Spec 211: every subtotal / total line passes through here, so the thousands
  // separators go on first and a "$" lands on "1,234.00", as on the cells.
  const footerCurrency = (col, rawHtml) => {
    const html = groupedDisplay(col, rawHtml, numericTotals, showThousands)
    if (!showDollarNumeric(col, numericTotals)) return html
    // CC-53905: ON puts a "$" only on a currency-declared column, onto the
    // subtotal / total CB displayed — never a re-parse padded to 2 decimals.
    if (forceCurrency === true) return currencyEligible(col, currencyMarked) ? withCurrencySymbol(html) : html
    if (forceCurrency === false) return stripCurrencySymbol(html)
    return html
  }
  const footerCellContent = (col, serverDisplay, visibleLeaves, allLeaves) => {
    const colId = String(col.columnId)
    const { safe, sum, kept } = footerRecompute({ colId, serverDisplay, visibleLeaves, allLeaves, isFiltered, recompute, summable })
    if (safe) {
      return { html: footerCurrency(col, formatLikeSample(serverDisplay)(sum)), kept: false }
    }
    return { html: footerCurrency(col, serverDisplay ?? ''), kept }
  }

  // A leaf cell's HTML. CC-49341: amount columns render as currency when toggled
  // on; CC-49341/CC-49743: explicit ON adds $, explicit OFF strips it; auto (null)
  // leaves the server display untouched. `cells` is the row's dataCells — or, for
  // a rolled statement parent, the page's rolledCells (spec 127 S5).
  const leafCellHtml = (cells, col) => {
    const cell = cellById(cells, col.columnId)
    // Spec 211: thousands separators first, so a "$" lands on "1,234.00";
    // spec 212: stripped instead while "Show commas in numbers" is off.
    const disp = groupedDisplay(col, cell?.displayValue ?? '', numericTotals, showThousands)
    // CC-53905: ON puts a "$" only on a currency-declared column (CB's
    // CellsFormat 'C', or an adapter column its source marks) and only onto the
    // digits displayed. Spec 133's neverCurrency columns (a count, a 'P'
    // fraction, AP's Past Due) stay excluded inside currencyEligible. Which
    // columns are numeric for it is `showDollarNumeric` (review fix 4).
    return !showDollarNumeric(col, numericTotals)
      ? disp
      : forceCurrency === true
        ? (currencyEligible(col, currencyMarked) ? withCurrencySymbol(disp) : disp)
        : forceCurrency === false
          ? stripCurrencySymbol(disp)
          : disp
  }

  /*
   * Spec 127 S8 — the roll-up toggle, in the tree column's cell, BEFORE the cell's
   * HTML: siblings, never nested, the same rule as the group header (CC-49837),
   * because the cell can hold an account link and an <a> inside a <button> is
   * invalid interactive-in-interactive HTML.
   *
   * A native <button>, so Enter and Space work with no key handler (I5). The row
   * keys on the tree id (S7) and the button is the same element in both states —
   * only its attributes change — so focus stays on it while its descendants
   * disappear. Name "Roll up {account}" / "Expand {account}", aria-expanded.
   *
   * Contrast: text-gray-500 is Tailwind 4.2.1's oklch(55.1% 0.027 264.364) =
   * #6a7282, 4.84:1 on the white row and 4.63:1 on its hover:bg-gray-50 (#f9fafb)
   * (computed from the oklch tokens in node_modules/tailwindcss/theme.css) —
   * SC 1.4.11 asks 3.0:1 of a graphical control. h-6 w-6 is the 24x24 target
   * spec 121 applies; -my-1 keeps the row's height. No size utility sits beside a
   * .btn-* class here, so src/index.css cannot silently defeat one.
   *
   * ▾ / ▸ match the group toggle. CalendarReport.test.jsx:97 finds GROUP toggles
   * by /[▾▸]/ — this toggle renders only in statement mode, never on the Calendar
   * (I16).
   *
   * A row with no toggle (a Total line, a leaf account, a row without `tree`)
   * gets an equal empty box so every label in the column starts at the same x.
   *
   * ON PAPER the toggle is `data-print-hide` (print-report.css: display:none), so
   * the empty box must be too — otherwise every rollable parent prints 28px (the
   * 24px box + the gap) LEFT of its siblings and of its own Total line, and a
   * depth-1 parent prints left of a depth-0 leaf (review of spec 127, measured in
   * a real browser with the built CSS). Both leading boxes go, so every label
   * loses the same width and the indent is depth × 16px alone, as on screen.
   */
  const renderTreeCell = (t, html) => {
    const rollable = !!t && t.kind !== 'close' && tree.rollable.has(t.id)
    const rolled = rollable && tree.rolled.has(t.id)
    const depth = t ? Number(t.depth) || 0 : 0
    return (
      <span className="inline-flex items-start gap-1" style={{ paddingLeft: `${depth * 16}px` }}>
        {rollable ? (
          <button
            type="button"
            onClick={() => toggleRolled(t.id)}
            aria-expanded={!rolled}
            aria-label={`${rolled ? 'Expand' : 'Roll up'} ${stripHtml(html).trim()}`}
            data-print-hide
            data-pendo="report-rollup-one"
            className="inline-flex h-6 w-6 -my-1 flex-none items-center justify-center text-gray-500 hover:text-gray-900"
          >
            <span aria-hidden="true">{rolled ? '▸' : '▾'}</span>
          </button>
        ) : (
          <span aria-hidden="true" data-print-hide className="inline-block h-6 w-6 -my-1 flex-none" />
        )}
        <span dangerouslySetInnerHTML={{ __html: html }} />
      </span>
    )
  }

  const keptMarker = (label) => (
    <span
      role="img"
      aria-label={`Server value: “${label}” isn’t a simple sum (an average, rate, or weighted figure), so re-adding only the visible rows would be incorrect. This total keeps the server value for the full dataset.`}
      title={`“${label}” isn’t a simple sum (e.g. an average or rate), so re-adding only the visible rows would give a wrong number. It keeps the server-calculated value for the full dataset.`}
      className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-400 text-[9px] leading-none text-gray-500 cursor-help"
    >
      i
    </span>
  )

  // Recursive renderer: data rows (filtered + sorted within their group) and
  // groups. baseGroupIndex keeps collapse keys stable across display pages.
  const renderRows = (rowsArg, level = 0, keyPrefix = '', baseGroupIndex = 0) => {
    const groups = rowsArg.filter((r) => r.isGroup)
    const leaves = processLeafRows(rowsArg)
    return (
      <>
        {leaves.map((row, i) => {
          // Statement mode: a tree row keys on its id (S7) and a rolled parent
          // shows the page's rolledCells (S5). Off statement mode `tree` is null
          // and this is the row the 86 grouped reports always rendered.
          const t = tree ? tree.treeOf(row) : null
          const cells = tree ? tree.displayCells(row) : row.dataCells
          return (
            <tr key={t ? treeRowKey(t) : `${keyPrefix}d-${i}`} className="hover:bg-gray-50">
              {visibleColumns.map((col) => {
                // CC-49460: wrap long values (matter numbers, emails, URLs) instead
                // of clipping; align-top keeps multi-line cells readable.
                const tdClass = `${PENDO_IGNORE} px-3 py-2 text-sm text-gray-800 border-b border-rc-line break-words align-top ${alignClass(col)} ${isPinnedCol(col) ? PIN_CELL_CLASS : ''}`
                // Cap a runaway auto-sized column so one long value can't starve
                // the rest of the table (see MAX_AUTO_COLUMN_PX). An explicitly
                // resized column keeps the width the user chose.
                const tdStyle = widths[String(col.columnId)] ? undefined : { maxWidth: MAX_AUTO_COLUMN_PX }
                if (tree && treeColumnId && String(col.columnId) === treeColumnId) {
                  return (
                    <td key={col.columnId} className={tdClass} style={tdStyle}>
                      {renderTreeCell(t, leafCellHtml(cells, col))}
                    </td>
                  )
                }
                return (
                  <td
                    key={col.columnId}
                    className={tdClass}
                    style={tdStyle}
                    dangerouslySetInnerHTML={{ __html: leafCellHtml(cells, col) }}
                  />
                )
              })}
            </tr>
          )
        })}
        {groups.map((g, i) => {
          // Hide groups with no matching rows for column filters AND for the
          // aging window (CC-49327 — an empty group is noise when hunting
          // stale matters). The hide-$0/inactive toggles keep their shipped
          // behavior: emptied groups stay visible with server subtotals.
          if ((activeFilters.length || agingActive) && !groupHasMatches(g.dataRows)) return null
          const key = groupRowKey(keyPrefix, level, baseGroupIndex + i)
          const isCollapsed = isGroupCollapsed(key)
          const groupAllLeaves = collectLeaves(g.dataRows)
          const groupVisibleLeaves = isFiltered ? groupAllLeaves.filter(rowPasses) : groupAllLeaves
          return (
            <Fragment key={key}>
              <tr className="bg-gray-50">
                <td
                  colSpan={colSpan}
                  className={`${PENDO_IGNORE} px-3 py-2 text-sm font-semibold text-gray-700 border-b border-rc-line`}
                  style={{ paddingLeft: `${12 + level * 16}px` }}
                >
                  {/* CC-49837: the disclosure control and the header content are
                      SIBLINGS, not parent/child. groupHeaderContent can contain an
                      anchor (General Ledger links its section headers to the
                      account register), and an <a> inside a <button> is invalid
                      interactive-in-interactive HTML — the anchor is not reachable
                      as its own control, and a click on it bubbled to the button so
                      the group collapsed AND the app navigated away at the same
                      time. Keeping them side by side fixes both. */}
                  {/* CC-49462: the group header spans every column, so it can't
                      carry the pinned-cell class. Sticking the CONTENT instead
                      keeps the group's name on screen while the user scrolls a
                      wide report sideways — otherwise the rows stay labelled by
                      the frozen column but the group they belong to scrolls away. */}
                  <span className={`inline-flex items-center gap-1.5 ${pinnedColumnId ? 'sticky left-0' : ''}`}>
                    {/* Spec 127 S1: a statement section has no disclosure control
                        — the heading is CB's section name and nothing more. */}
                    {!statementMode && (
                    <button
                      type="button"
                      onClick={() => toggleCollapse(key)}
                      aria-expanded={!isCollapsed}
                      aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${stripHtml(g.groupHeaderContent || '') || 'group'}`}
                      data-print-hide
                      className="inline-flex items-center bg-transparent border-none p-0 text-left text-gray-500 hover:text-gray-900"
                    >
                      {/* CC-49843: the tree glyph never reaches paper — print
                          already respects collapse state, no arrows needed.

                          B6 (spec 117) deliberately LEAVES these two glyphs. They
                          are a tree EXPANDER, not a dropdown caret — ▸/▾ is the
                          disclosure convention and a chevron-down in the collapsed
                          state would read as "open a menu". They are also load
                          bearing for a test: CalendarReport.test.jsx:97 finds these
                          buttons by matching /[▾▸]/ on textContent, so replacing
                          them with an <svg> would empty the text and the filter
                          would select nothing — a silent zero-match, not a red
                          assertion. */}
                      <span aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
                    </button>
                    )}
                    {/* eslint-disable-next-line react/no-danger */}
                    <span dangerouslySetInnerHTML={{ __html: g.groupHeaderContent || '' }} />
                  </span>
                </td>
              </tr>
              {!isCollapsed && renderRows(g.dataRows || [], level + 1, `${key}-`)}
              {/* CC-50050: a group can close with MORE THAN ONE subtotal line —
                  Production & Origination closes every section with Production /
                  Origination / Total. Same contract as the report-level
                  `reportFooterRows`: render the whole block CB sent, in CB's order,
                  values verbatim, and only line 0 takes part in the filtered-total
                  recompute (it is the one the SUM-only guardrail classified).
                  Spec 196: every line follows Show $ (footerCurrency). */}
              {groupFooterLines(g).map((cells, footIdx) => (
                <tr key={`${key}-foot-${footIdx}`} className="bg-gray-50/50 font-medium cb-pin-tint-soft">
                  {visibleColumns.map((col) => {
                    const server = cellById(cells, col.columnId)?.displayValue ?? ''
                    const { html, kept } = footIdx === 0
                      ? footerCellContent(col, server, groupVisibleLeaves, groupAllLeaves)
                      : { html: footerCurrency(col, server), kept: false }
                    return (
                      <td
                        key={col.columnId}
                        className={`${PENDO_IGNORE} px-3 py-1.5 text-sm text-gray-800 border-b border-rc-line ${alignClass(col)} ${isPinnedCol(col) ? PIN_CELL_CLASS : ''}`}
                      >
                        {/* eslint-disable-next-line react/no-danger */}
                        <span dangerouslySetInnerHTML={{ __html: html }} />
                        {kept && keptMarker(col.label)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </Fragment>
          )
        })}
      </>
    )
  }

  const grandAllLeaves = collectLeaves(rows)
  const grandVisibleLeaves = isFiltered ? grandAllLeaves.filter(rowPasses) : grandAllLeaves

  /*
   * The four display toggles. Handed to this grid's own Display View popover,
   * the same place ReportDataGrid puts them via DataGrid's `displayViewExtras`
   * (spec 092). Until now this grid mirrored them as a naked row below the
   * toolbar instead, so the 86 grouped reports kept the scruffy strip the
   * refresh removed from the 33 flat ones.
   *
   * `pageDisplayExtras` is the same section's slot for a PAGE's own instant
   * view controls (Aged A/R's hide-zero / hide-inactive / practice area), so a
   * page no longer needs a floating row of its own to host them.
   *
   * B4 + B5 (spec 117) — the same single defect fixed on the flat grid, and it
   * had to be fixed in BOTH places for the same reason this whole block is
   * duplicated: the two grids mirror each other and 86 grouped reports come
   * through here while 33 flat ones go through ReportDataGrid. The rows above
   * this popover (:1481) were already the drawn control; these were native
   * inputs whose `rounded border-rc-control` painted nothing (Tailwind v4
   * preflight zeroes `border-width` on `*` and no bare `border` width sat beside
   * the colour), on rows with no hover.
   *
   * `-mx-3.5 px-3.5` cancels the `px-3.5` wrapper at :1497 for the hover band and
   * restores it for the content. `gap-2` between rows is kept — see the matching
   * note in ReportDataGrid.jsx.
   */
  /* `has-[:focus-visible]`, not `focus-within` — see the note on the identical
     constant in ReportDataGrid.jsx: `:focus-within` matches a MOUSE click on the
     label and left the band stuck on a clicked row. Keyboard-only now.

     On THIS grid the band is exact parity: the column button 210 lines below
     (:1499) is already `hover:bg-gray-50 focus-visible:bg-gray-50`, as are
     Freeze first column (:1534) and Reset layout (:1591). It is only the FLAT
     grid's column button (DataGrid.jsx:1161) that has no band, which is why the
     note over there calls the same line an addition. The full picture is in
     ReportDataGrid.jsx; do not re-derive it from one grid.

     KEYBOARD MODEL GAP, disclosed not fixed (spec 117 §5 G6). This popover is a
     PopoverMenu with the default `role="menu"`, and its Arrow/Home/End roving
     matches ITEM_SELECTOR only — `[role="menuitem"],[role="menuitemcheckbox"],
     [role="menuitemradio"]` (PopoverMenu.jsx:37) — calling preventDefault. The
     column rows are `menuitemcheckbox` buttons and ARE roved; these rows are
     `<label><input type="checkbox">` with no role and are NOT, so ArrowDown
     walks the column list and jumps straight past them to Freeze first column.
     Tab reaches them, so it is not a clean SC 2.1.1 failure, but it pre-dates
     this sweep and the sweep made it INVISIBLE: these rows used to look
     different (`inline-flex gap-1.5`, no hover, no band) and now have the roved
     rows' exact geometry and bands. Fixing it means widening PopoverMenu's
     ITEM_SELECTOR or re-rolling these as menuitemcheckbox buttons with a
     presentational box — PopoverMenu.jsx is outside this stream's partition and
     the second option rewrites six suites' `getByRole('checkbox')` reads.

     ⚠️ MOVED 2026-09-09 (spec 121 gap 2): the string itself now lives beside the
     control it pairs with, exported from ControlCheckbox.jsx, because a THIRD site
     needs it — ARReport.jsx's two `pageDisplayExtras` toggles render as siblings of
     these rows and had `gap-1.5` with no band, which is half of B4/B5 reproduced by
     a file partition. Everything above still describes it. */
  const displayToggles = (currencyActs || thousandsActs || (numericColIds.length > 0 && showZeroToggle) || (hasInactiveRows && showInactiveToggle) || hasAgingDates || pageDisplayExtras) ? (
      <div className="flex flex-col gap-2">
        {currencyActs && (
          <label className={TOGGLE_ROW}>
            <ControlCheckbox
              checked={forceCurrency ?? serverShowsCurrency}
              data-pendo="report-currency-toggle"
              onChange={(e) => {
                trackGridTransform(storageKey, 'currency_toggle', e.target.checked ? 'on' : 'off')
                setForceCurrency(e.target.checked)
              }}
            />
            Show $ on amounts
          </label>
        )}
        {thousandsActs && (
          <label className={TOGGLE_ROW}>
            <ControlCheckbox
              checked={showThousands}
              data-pendo="report-thousands-toggle"
              onChange={(e) => {
                trackGridTransform(storageKey, 'thousands_toggle', e.target.checked ? 'on' : 'off')
                setShowThousands(e.target.checked)
                if (storageKey) prefs.setPageFlag?.(storageKey, THOUSANDS_PAGE_FLAG, e.target.checked)
              }}
            />
            Show commas in numbers
          </label>
        )}
        {numericColIds.length > 0 && showZeroToggle && (
          <label className={TOGGLE_ROW}>
            <ControlCheckbox
              checked={hideZero}
              onChange={(e) => setHideZero(e.target.checked)}
            />
            Hide $0 / zero-balance rows
          </label>
        )}
        {hasInactiveRows && showInactiveToggle && (
          <label className={TOGGLE_ROW}>
            <ControlCheckbox
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
            />
            Hide inactive / closed
          </label>
        )}
        {hasAgingDates && (
          <span
            className="inline-flex items-center gap-1.5"
            title={`Show only rows whose “${agingCol.label}” is more than the chosen number of days ago. Rows with no date count as never — they stay shown.`}
          >
            <span>No activity in</span>
            <select
              aria-label={`Inactivity window (${agingCol.label})`}
              value={agingMode}
              onChange={(e) => {
                setAgingMode(e.target.value)
                if (e.target.value !== 'custom') setCustomDays('')
              }}
              // ADA: border-rc-control (#8b95a5, 3.03:1), never border-gray-200
              // (#e5e7eb, 1.24:1) — on a white-on-white control the border is the
              // only thing identifying it, so it owes SC 1.4.11 its 3.0:1. Focus
              // moves to rc-chip-text (#1d4ed8, 6.70:1) because cb-accent
              // (#479eff, 2.76:1) would have made contrast DROP on focus once the
              // resting border cleared 3.0:1. Geometry stays 22px/4px on purpose:
              // this is a dense control inside the Display View popover, the same
              // exception .input-rc documents — it is not a Bar-1 button.
              className="border border-rc-control rounded px-1.5 py-0.5 text-xs focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text"
            >
              <option value="">Off</option>
              {AGING_PRESET_DAYS.map((d) => (
                <option key={d} value={String(d)}>{d}+ days</option>
              ))}
              <option value="custom">Custom…</option>
            </select>
            {agingMode === 'custom' && (
              <span className="inline-flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  value={customDays}
                  aria-label="Custom inactivity days"
                  placeholder="days"
                  onChange={(e) => setCustomDays(e.target.value)}
                  // ADA: same treatment as the window <select> above.
                  className="w-16 border border-rc-control rounded px-1.5 py-0.5 text-xs focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text"
                />
                <span>days</span>
              </span>
            )}
            <span className="text-gray-400">({agingCol.label})</span>
          </span>
        )}
        {pageDisplayExtras}
        {/*
          A page that contributes controls this reset cannot reach says so, out
          loud, in the menu — see PAGE_EXTRAS_NOT_RESET_NOTE. Byte-identical to
          the flat grid's copy, deliberately: a caveat that appears on 33 reports
          and not on 86 is its own drift. Rendered ONLY when the hook is missing,
          so Aged A/R (which now passes it) never shows it.

          --color-rc-stale-text #92400e on the panel's bg-white computes to
          7.09:1; same 11px/stale-text pair ReportExport.jsx uses for its own
          in-panel caveat.
        */}
        {pageDisplayExtras && !onResetPageDisplayExtras && (
          <span data-testid="page-extras-not-reset" className="text-[11px] text-rc-stale-text">
            {PAGE_EXTRAS_NOT_RESET_NOTE}
          </span>
        )}
      </div>
  ) : null
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Grid-owned query controls (Group by / Filter) on their own row above the
          record count — the twin of DataGrid.jsx's slot, and it MUST exist here.
          Accepting the prop without rendering it is not a cosmetic miss: choosing
          a group swaps a flat report to THIS component, so the pills would unmount
          at the exact moment the user needs them and there would be no way to
          ungroup or clear the filter without re-running the report. Worse, a
          legacy grouped CSV renders here from the start (showGrouped is also true
          when the data already carries group rows), so those reports would never
          show the controls at all. */}
      {headerRow && (
        <div className="border-b border-gray-100 px-4 py-2.5">{headerRow}</div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="flex min-w-0 items-baseline gap-2 text-[13px] font-semibold text-rc-ink">
          {report.recordCount != null
            ? `${formatCount(report.recordCount)} ${report.recordCount === 1 ? 'record' : 'records'}`
            : 'Grouped report'}
          {/* CC-49874: name the group count too. V1's status line counts the
              proc's pre-split rows (e.g. "34 of 34" on Aged AP Detail) while
              recordCount counts the detail rows CB returns (68 there — one
              Bills + one Unapplied payment line per vendor, identical to what
              V1 renders). Showing both makes the difference self-explanatory
              instead of reading as duplicated data. */}
          {rows.filter((r) => r.isGroup).length > 0 && (
            <span> · {formatCount(rows.filter((r) => r.isGroup).length)} groups</span>
          )}
          {isFiltered && <span className="ml-2 text-gray-500">· filtered</span>}
        </div>
        <div className="flex items-center gap-2">
        {/* Expand/collapse-all (CC-49905). Actions, not view options, so they
            stay on the strip beside Display View rather than inside it. */}
        <span className="inline-flex items-center gap-2">
          {/* btn-rc, not btn-cb-toolbar: they now sit next to Display View and
              Column Filter, and the two geometries side by side read as a bug.
              B3: the `h-[30px] text-[13px]` these used to carry alongside .btn-rc
              was DEAD CODE and always had been — src/index.css is UNLAYERED and
              Tailwind v4 emits utilities inside @layer utilities, so .btn-rc's own
              38px/14px won regardless of specificity. Deleted rather than left to
              tell the next reader these are 30px controls. Do not re-add a height
              or size utility next to .btn-rc / .select-rc / .input-rc. */}
          {/* Spec 127 S2: on a statement the pair is "Expand all" / "Roll up all"
              and acts on PARENT ACCOUNTS, never on sections — and only when the
              statement has a parent to roll up. Same .btn-rc, no size utility
              beside it (index.css is unlayered). */}
          {statementMode ? (hasRollable && (
            <>
              <button type="button" data-pendo="report-rollup-expand-all" onClick={expandAllRolled} className="btn-rc">
                Expand all
              </button>
              <button type="button" data-pendo="report-rollup-all" onClick={rollUpAll} className="btn-rc">
                Roll up all
              </button>
            </>
          )) : (
            <>
              <button type="button" data-pendo="report-expand-all" onClick={() => setAllCollapsed(false)} className="btn-rc">
                Expand all
              </button>
              <button type="button" data-pendo="report-collapse-all" onClick={() => setAllCollapsed(true)} className="btn-rc">
                Collapse all
              </button>
            </>
          )}
        </span>
        {/* Host-owned strip buttons (Rename columns) sit IMMEDIATELY left of
            Display View, matching DataGrid's order on the flat path. The first
            pass put them ahead of Expand/Collapse, which left two buttons
            between Rename columns and the panel it belongs with — the request
            was "next to Display View", and on a grouped report it was not. */}
        {stripActions}
        {/*
          spec 092 — the same result strip the flat path gets, so all 140 reports
          share one grid chrome. This grid does NOT render DataGrid (it builds its
          own table for server subtotals), so the strip is mirrored here rather
          than inherited. If these two ever diverge, 86 grouped reports quietly
          get a different UI from the 33 flat ones — which is the split spec 092
          exists to close.
          Filters was already a toggle here; it keeps its behaviour and takes the
          mock's name. Freeze, Reset grid layout and Columns fold into Display View.
        */}
        {/*
          E4 (2026-08-27) — Display View + Column Filter are ONE segmented control,
          the TWIN of DataGrid.jsx's wrapper on the flat path. Both files, always:
          this strip is mirrored, not inherited, so an edit made in one place looks
          exactly like a finished job while 86 or 33 reports keep the old UI.

          Two buttons, two accessible names, one visual control — see the note in
          DataGrid.jsx for why they must not merge (the e2e audit measures each by
          name). The seam lives in src/index.css as .btn-rc-seg-l/-r; do not
          reach for a `rounded-r-none` utility here, it would be silently defeated.

          The wrapper adds no <button> and reorders none, so Rename columns stays
          the button immediately before Display View (stripActionsPlacement.test.jsx).
          Column Filter is unconditional on this path — unlike the flat grid, which
          gates it on `enableFiltering` — so the pair is always both halves here.
        */}
        <span className="inline-flex items-center">
        <PopoverMenu
          label="Display view options"
          triggerLabel="Display View"
          width={288}
          triggerClassName="btn-rc btn-rc-seg-l"
          triggerContent={<><SlidersIcon className="h-3.5 w-3.5" /> Display View</>}
          data-pendo="report-display-view"
        >
          {/* A2 (2026-08-26): the panel was cramped — 5px row padding under a
              2px section heading, in a 220px well. Rows go to 7px and the well to
              264px, which keeps the same number of rows visible before scrolling
              (the row grew 4px, the well 44px) while giving each one air. Only the
              VERTICAL rhythm moves; px-3.5 is unchanged so the checkbox column
              still lines up with the section headings and the footer actions. */}
          <p className="px-3.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-rc-muted">Columns</p>
          <div className="max-h-[264px] overflow-y-auto">
            {baseColumns.map((col) => (
              <button
                key={col.columnId}
                type="button"
                role="menuitemcheckbox"
                aria-checked={!isHidden(col)}
                onClick={() => toggleColumn(col)}
                className="flex w-full items-center gap-2.5 px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
              >
                {/* `border-rc-seam`, moved off `border-rc-control` 2026-09-09.
                    This row is `hover:bg-gray-50 focus-visible:bg-gray-50`
                    (see its button above), so the unchecked box's 1px boundary
                    — the ONLY thing drawing a white-on-white control — sat at
                    2.896:1 on #f9fafb whenever a pointer or keyboard focus was
                    on the row, under SC 1.4.11's 3.0:1. rc-seam #6b7280 is
                    4.834 / 4.626 on the two grounds this box renders on.
                    It also puts this popover back on ONE colour: the View
                    options rows below take the same token through
                    <ControlCheckbox>, and two greys in one list is Himaad's B4
                    complaint pointing the other way. DataGrid.jsx:1165 (flat)
                    and ReportExport.jsx:306 carry byte-identical copies of this
                    string and are other streams' files — spec 117 §5 G1. */}
                <span
                  aria-hidden="true"
                  className={`grid h-3.5 w-3.5 flex-none place-items-center rounded-[3px] border ${!isHidden(col) ? 'border-rc-chip-text bg-rc-chip-text text-white' : 'border-rc-seam bg-white'}`}
                >
                  {!isHidden(col) && (
                    <svg viewBox="0 0 10 8" className="h-2 w-2.5" fill="none" stroke="currentColor" strokeWidth={2.2}>
                      <path d="M1 4l2.5 2.5L9 1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="min-w-0 flex-1 truncate">{col.label || `Column ${col.columnId}`}</span>
              </button>
            ))}
          </div>
          {displayToggles && (
            <>
              <div className="my-1.5 h-px bg-rc-line" />
              <p className="px-3.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-rc-muted">View options</p>
              <div className="px-3.5 pb-1 text-[13px] text-rc-ink-2">{displayToggles}</div>
            </>
          )}
          <div className="my-1.5 h-px bg-rc-line" />
          {/* CC-49462: freeze/unfreeze the identity column. */}
          {storageKey && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={!!pinnedColumnId}
              onClick={togglePin}
              // Nothing visible to freeze (every column hidden) — disabled rather
              // than inert, so the control still describes reality.
              disabled={!pinnedColumnId && !pinTargetId}
              title="Keep the first column visible while you scroll a wide report sideways"
              className="flex w-full items-center px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none disabled:opacity-40"
            >
              {pinnedColumnId ? 'Unfreeze first column' : 'Freeze first column'}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            /* E4: this attribute was MISSING on the grouped copy while the flat
               copy has carried it since spec 094 — so every "reset layout" event
               Pendo has recorded came from the 33 flat reports and none from the
               86 grouped ones, and the metric read as low usage rather than as
               half-instrumented. The VALUE matches DataGrid.jsx exactly, and is
               deliberately NOT re-keyed for the rename below: Pendo has been
               collecting on QA since 2026-08-26 and a new key would fork the
               series. */
            data-pendo="report-reset-layout"
            onClick={handleReset}
            /*
              E4 verify pass (2026-08-28) — the twin of DataGrid.jsx's fix, and
              it must stay worded the same. "to the report's default" was wrong:
              all four view options initialise from `initialToggles` (a saved
              view or a reopened job), so a view seeding hideZero:true has THAT
              as its default for this mount and the reset overrides it with the
              built-in value. See gridDisplayDefaults.js for why that divergence
              is deliberate and must stay row-revealing.

              "its own" excludes a page's `pageDisplayExtras`; when a page
              supplies those without a reset hook the menu itself carries
              PAGE_EXTRAS_NOT_RESET_NOTE, which is where a user will actually
              read it. Unlike the flat grid this component CAN see that
              configuration — the clause is still unconditional here on purpose,
              so the two tooltips cannot drift apart over a distinction the
              caveat line already makes.

              ~~"...column filters, GROUPING and its own view options..."~~
              SUPERSEDED 2026-08-28, and it was this batch that introduced it —
              at HEAD (fdcd114) this tooltip read "Reset columns, sort, and
              filters to this report's default" and named no grouping at all.
              `handleReset` above does not touch grouping and there is no
              grid-owned grouping state to touch: CB computes the group
              structure server-side (`report.reportGroupData` -> the `isGroup`
              rows this component renders VERBATIM), so nothing in this grid can
              restore, change or undo it. The word promised 86 grouped reports a
              capability that does not exist anywhere in the component.

              What the reset genuinely does to groups is re-EXPAND them
              (`setCollapsed({})` + `setCollapseDefault(false)`), which is
              collapse state, not grouping. It is left out of the sentence
              deliberately rather than reworded: the flat twin has no groups and
              so can never carry the clause, and this tooltip's whole contract
              above is that the two stay identical. The behaviour is pinned by
              its own case instead — resetGridLayout.grids.test.jsx's
              grouped-only "re-expands collapsed groups". Say it here only if
              the flat/grouped parity rule is dropped first.
            */
            title="Reset this grid's columns, widths, sort, column filters and its own view options to the built-in defaults — not to a saved view's. Does not change the report's filters or re-run it."
            className="flex w-full items-center px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
          >
            {/* E4 (2026-08-27) — "Reset layout" -> "Reset grid layout", the twin
                of DataGrid.jsx's rename. A report page now shows two resets and
                each must state its own scope: "Reset filters" in the query card
                resets filter VALUES and re-runs the report; this one resets
                LAYOUT and cannot change a figure the server sent. */}
            Reset grid layout
          </button>
        </PopoverMenu>

        <button
          type="button"
          data-pendo="report-column-filter"
          onClick={() => setShowFilters((s) => !s)}
          aria-expanded={filtersVisible}
          aria-pressed={filtersVisible}
          title="Filter the rows on screen by a column's values"
          className={`btn-rc btn-rc-seg-r ${filtersVisible ? 'is-active' : ''}`}
        >
          <FunnelIcon className="h-3.5 w-3.5" />
          Column Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>
        </span>
        </div>
      </div>

      {/* CC-49825 (QA rejection): the fixed-height scroll container CLIPS on
          paper — page 1 printed, later pages were blank even with every group
          rendered. print:* lifts the clamp; the sticky header goes static. */}
      <div ref={scrollRef} className="overflow-auto max-h-[calc(100vh-15rem)] print:overflow-visible print:max-h-none">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        {/* role="table" + a real name (CC-50173) — see the note in DataGrid. The
            report's own name is the best name available here; `report.name` comes
            from CB's payload, so it degrades to the generic wording rather than to
            an unnamed table. */}
        <table
          ref={tableRef}
          role="table"
          aria-label={report?.name ? `${report.name} results` : 'Grouped report results'}
          /*
           * CC-53347 — `w-full` (a DEFINITE width) is what makes `table-fixed` real.
           * `min-w-full` leaves `width: auto`, and an auto-width table silently
           * falls back to the automatic algorithm the moment its content overflows,
           * discarding every per-column width while still reporting
           * `tableLayout: "fixed"`. See the long note on `widths` above for the
           * measured differential.
           *
           * WHY THE UNSIZED CASE KEEPS `min-w-full`. With no user widths at all we
           * WANT content sizing — that is the good behaviour on 86 grouped reports,
           * and `MAX_AUTO_COLUMN_PX` exists to stop one long value starving the
           * rest. Switching to `w-full` unconditionally would squeeze every wide
           * report to the viewport. So the definite width arrives only once the
           * user has expressed a width, at which point the seed has frozen the
           * other columns and nothing else moves.
           *
           * Horizontal scroll SURVIVES: measured, with `w-full` + `table-fixed` and
           * seeded widths summing past the container, the table expands to their sum
           * and the wrapper scrolls (1401px inside a 600px box). `w-full` sets a
           * definite width, it does not cap one.
           */
          className={anyWidths ? 'w-full table-fixed' : 'min-w-full'}
        >
          {/* B4 — THE DOUBLE-DRAWN HEADER RULE. This <thead> used to carry
              `border-b border-gray-100` AND every <th> carries a `border-b` of
              its own (GroupedHeaderCell), so the two STACKED: the 86 grouped
              reports drew a ~2px composite separator under the header band where
              the 33 flat reports draw a single 1px (DataGrid's <th> has no border
              of its own, only its <thead>). The thead half is the redundant one
              and is gone; the per-th rule stays, now routed through
              --color-rc-line. Do not re-add a border here. */}
          <thead className="bg-gray-50 sticky top-0 z-20 print:static">
            <tr>
              <SortableContext items={visibleColumns.map((c) => String(c.columnId))} strategy={horizontalListSortingStrategy}>
              {visibleColumns.map((col, idx) => {
                const id = String(col.columnId)
                // CC-49331: show the level index on secondary/tertiary sorts.
                const lvlIdx = sorts.findIndex((x) => x.id === id)
                const lvl = lvlIdx >= 0 ? sorts[lvlIdx] : null
                const dir = lvl ? `${lvl.desc ? '▼' : '▲'}${sorts.length > 1 ? '¹²³'[lvlIdx] : ''}` : ''
                // CC-49333: expose the sort state to AT (mirrors DataGrid /
                // ReportDataGrid). aria-sort belongs on ONE header at a time,
                // so with multi-level sorts only the level-1 column carries it.
                const ariaSort = lvlIdx === 0 && lvl ? (lvl.desc ? 'descending' : 'ascending') : undefined
                // CC-49944: row evidence decides whether this reads as numeric.
                const definition = columnDefinition(col, {
                  dataKind: dataKinds.get(id),
                  hasTotal: totaledIds.has(id),
                  sample: samples.get(id),
                })
                return (
                  <GroupedHeaderCell
                    key={col.columnId}
                    id={id}
                    label={col.label}
                    width={widths[id]}
                    onResize={(w) => setColWidth(id, w)}
                    onResetWidth={() => resetColWidth(id)}
                    ariaSort={ariaSort}
                    pinned={isPinnedCol(col)}
                    isLastHeader={idx === visibleColumns.length - 1}
                  >
                      {/* Spec 127 S3: a statement header is a label, not a
                          control — no sort button, no aria-sort (ariaSort is
                          undefined because `sorts` is empty). Same typography. */}
                      {statementMode ? (
                        <span className={`flex items-center gap-1 font-semibold text-[13px] text-gray-700 ${alignClass(col)}`}>
                          {col.label}
                        </span>
                      ) : (
                      <button
                        type="button"
                        onClick={(e) => toggleSort(col, e)}
                        title="Click to sort · Shift+click to add a secondary sort"
                        aria-label={`Sort by ${col.label}`}
                        className={`flex items-center gap-1 bg-transparent border-none p-0 font-semibold text-[13px] text-gray-700 ${alignClass(col)}`}
                      >
                        {col.label}
                        {/* B4: the indicator recedes — rc-muted at 10px, not
                            accent-blue at the label's own size, so a sorted
                            header reads as a header with a mark rather than as
                            two competing pieces of text. The STRING is untouched:
                            `dir` fuses the direction glyph with the multi-sort
                            level superscript (see its construction above), and
                            ReportGroupedGrid.test.jsx asserts this button's
                            textContent contains ¹/² — style it, never rebuild it. */}
                        {dir && <span className="text-rc-muted text-[10px] leading-none">{dir}</span>}
                      </button>
                      )}
                      {/* D3 (2026-08-27): a definition that is only the column's
                          type plus the generic sort/filter hint earns no icon. The
                          predicate is the SAME function the flat grid's gate in
                          DataGrid.jsx calls (./columnDefinitionGate), so these two
                          cannot drift apart. */}
                      {isAuthoredDefinition(definition) && (
                        <span
                          role="img"
                          aria-label={`About ${col.label}: ${definition}`}
                          title={definition}
                          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-400 text-[9px] leading-none text-gray-500 cursor-help"
                        >
                          i
                        </span>
                      )}
                  </GroupedHeaderCell>
                )
              })}
              </SortableContext>
            </tr>
            {/* A3 round 2 — the filter cells' pt-3 pb-2.5 (was py-1.5) is the
                separation from the header label above, and is kept byte-identical
                to src/components/common/DataGrid.jsx's filter row on purpose.

                Filter row stays mounted but collapses to display:none when hidden
                (de-cluttered by default) — the toggle / an active filter reveals it. */}
            <tr className={filtersVisible ? 'border-b border-rc-line' : 'hidden'}>
              {visibleColumns.map((col) => (
                <th key={col.columnId} scope="col" className={`px-3 pt-3 pb-2.5 ${isPinnedCol(col) ? PIN_CELL_CLASS : ''}`}>
                  {/* Spec 171: `matchModes` opts this grid into the operator
                      select (Contains / Starts with / Ends with). Matching goes
                      through matchColumnFilter in `passesOther`, the same
                      predicate the subtotals, grand total and export view read. */}
                  {isColumnFilterable(col) ? (
                    <ColumnValuePicker
                      label={col.label}
                      value={filters[String(col.columnId)]}
                      pickerValues={pickerValuesByCol[String(col.columnId)]}
                      onChange={(v) => setFilter(col, v == null ? '' : v)}
                      matchModes
                      matchModeResetKey={filterModeResetKey}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 || (anyRowFilter && !groupHasMatches(rows)) ? (
              /* Bug #11: also show the empty state when active filters hide
                 every row — previously the body just went blank. */
              <tr>
                <td colSpan={colSpan} className="px-3 py-12 text-center text-sm text-gray-500">
                  No data for the selected filters
                </td>
              </tr>
            ) : (
              renderRows(pageRows, 0, '', pageGroupOffset)
            )}
          </tbody>
          {footerRows.length > 0 && (
            <tfoot className="bg-gray-100 font-semibold sticky bottom-0 z-10 print:static">
              {/* CC-49860: a report can close with MORE THAN ONE total row —
                  Timekeeper Analysis prints a PTD total and a YTD total. Render
                  the whole run CB sent, in CB's order, values verbatim. */}
              {footerRows.map((cells, rowIdx) => (
                <tr key={`footer-${rowIdx}`}>
                  {visibleColumns.map((col) => {
                    const server = cellById(cells, col.columnId)?.displayValue ?? ''
                    // Only the FIRST footer row participates in the filtered-total
                    // recompute — it is the one the SUM-only guardrail classified
                    // against. Any further rows print CB's digits as sent, and
                    // follow Show $ like row 0 (spec 196).
                    const { html, kept } = rowIdx === 0
                      ? footerCellContent(col, server, grandVisibleLeaves, grandAllLeaves)
                      : { html: footerCurrency(col, server), kept: false }
                    return (
                      <td
                        key={col.columnId}
                        className={`${PENDO_IGNORE} px-3 py-2 text-sm text-gray-900 border-t border-rc-line ${alignClass(col)} ${isPinnedCol(col) ? PIN_CELL_CLASS : ''}`}
                      >
                        {/* eslint-disable-next-line react/no-danger */}
                        <span dangerouslySetInnerHTML={{ __html: html }} />
                        {kept && keptMarker(col.label)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
        </DndContext>
      </div>

      {/* Group pager (CC-49616) — DataGrid pager parity; hidden on paper
          (printing renders every group anyway). ADA: Previous/Next carry
          border-rc-control (3.03:1), not the border-gray-200 (1.24:1) they
          shipped with — they are white-on-white outlined buttons, so the border
          is the only thing identifying them. Their rounded-lg (8px) is left
          alone: DataGrid's pager is byte-identical and the two must stay in
          step, so a radius change belongs in one pass across both. */}
      {paging && (
        <div data-print-hide className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Page {Math.min(pageIndex, pageCount - 1) + 1} of {pageCount}
            <span className="ml-2">· {visibleTop.filter((r) => r.isGroup).length} groups</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={pageIndex <= 0}
              className="px-3 py-1 text-xs font-medium rounded-lg border border-rc-control text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
              disabled={pageIndex >= pageCount - 1}
              className="px-3 py-1 text-xs font-medium rounded-lg border border-rc-control text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Filtered-totals coherence bar (CC-49382) — only while filtering. */}
      {footerCells.length > 0 && isFiltered && (
        <div className="flex items-center gap-3 px-4 py-2 border-t border-gray-100 text-xs">
          <span className={recompute ? 'text-gray-600' : 'text-amber-700'}>
            {recompute
              ? `Subtotals and the grand total reflect the ${grandVisibleLeaves.length} filtered row${grandVisibleLeaves.length === 1 ? '' : 's'} shown — uncheck to see the full unfiltered totals. Columns marked “i” aren’t simple sums (averages, rates) and keep their server value.`
              : 'Subtotals and the grand total reflect the full dataset — not your current filter. Check the box to total just the filtered rows.'}
          </span>
          {/* B4 (spec 117): the drawn control; no row hover, this is a bar. */}
          <label className="ml-auto inline-flex items-center gap-2.5 text-gray-600 cursor-pointer">
            <ControlCheckbox
              checked={recompute}
              onChange={(e) => setRecompute(e.target.checked)}
            />
            Total just the filtered rows
          </label>
        </div>
      )}
    </div>
  )
}

export default ReportGroupedGrid
